import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import ScannerMenu from './ScannerMenu';
import ConfirmationCandle from './ConfirmationCandle';
import ScanFindingsSummary from './ScanFindingsSummary';
import AppHeader from './AppHeader';
import SignalChartModal from './SignalChartModal';
import CopySymbol from './CopySymbol';
import StatusAge from './StatusAge';
import { formatLivePriceAge, formatScannedAge, getLivePriceChangePercent, subscribeToLivePriceClock } from '../lib/livePriceUtils';
import { isRequestAborted } from '../lib/httpUtils';
import { parseAutoVerifyInterval } from '../lib/autoVerifyUtils';
import { addAutoVerifyNotification, describeAutoVerifyNotification } from '../lib/autoVerifyNotifications';
import { buildLiveQuoteSubscription } from '../lib/liveQuoteSymbols';
import {
  getCachedSignalChartData,
  getSignalChartData,
  normalizeSignalChartInterval,
  subscribeSignalChartData,
} from '../lib/signalChartData';
import { SYMBOL_DATA } from '../constants/symbols';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AUTO_VERIFY_INTERVALS = [
  { value: 30, label: '30 seconds' },
  { value: 60, label: '1 minute' },
  { value: 120, label: '2 minutes' },
  { value: 300, label: '5 minutes' },
  { value: 900, label: '15 minutes' },
  { value: 3600, label: '1 hour (Recommended)' },
  { value: 86400, label: '1 day (Recommended)' },
];
// Final statuses never change again, so automatic verification skips them
const COMPANY_NAMES = new Map(SYMBOL_DATA.map(item => [item.symbol, item.name]));
// Last-live-price caching removed — only real-time WebSocket prices are shown.

// Helper function to format date in NY timezone
const formatDateNY = (dateString) => {
  if (!dateString) {
    return { date: '-', time: '' };
  }

  const date = new Date(dateString);

  const datePart = date.toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    year: '2-digit',
    month: 'short',
    day: 'numeric'
  });

  const timePart = date.toLocaleTimeString('en-US', {
    timeZone: 'America/New_York',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });

  return { date: datePart, time: `${timePart} ET` };
};

const formatCompactDateNY = (dateString) => {
  if (!dateString) return '-';

  const date = new Date(dateString);
  const datePart = date
    .toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      year: '2-digit',
      month: 'short',
      day: 'numeric',
    })
    .replace(/^Sep\b/, 'Sept');
  const timePart = date.toLocaleTimeString('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return `${datePart} ${timePart} ET`;
};

const formatCurrency = (value, fallback = '-') => {
  if (value === null || value === undefined) return fallback;
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return fallback;
  return `$${numeric.toFixed(2)}`;
};

const formatVolume = (value, fallback = '-') => {
  if (value === null || value === undefined) return fallback;
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return fallback;
  return numeric.toLocaleString('en-US');
};

const normalizeQuoteSymbolKey = (value) =>
  String(value || '').trim().toUpperCase().replace(/-/g, '.');

const getLiveQuoteForSymbol = (livePrices, symbol) => {
  const normalized = normalizeQuoteSymbolKey(symbol);
  const trimmed = String(symbol || '').trim();
  const upper = trimmed.toUpperCase();
  return livePrices[normalized] || livePrices[upper] || livePrices[trimmed] || null;
};

const getLivePriceValue = (liveQuote) => {
  const numeric = Number(liveQuote?.price);
  return Number.isFinite(numeric) ? numeric : null;
};

// Direction determines the indicator color at every price stage.
const getFlashColor = (scan) => scan.signal_type === 'CALL' ? 'flash-dot-green' : 'flash-dot-red';

// Whether the live price puts a row in the flashing state for entry/stop/target
const getFlashState = (scan, quote) => {
  const price = getLivePriceValue(quote);
  if (price === null || price === undefined) return { entry: false, stop: false, target: false };
  const isCall = scan.signal_type === 'CALL';
  const { entry_price: entry, stop_price: stop, target_price: target } = scan;
  return {
    entry: !!(entry && target && (isCall ? price > entry && price < target : price < entry && price > target)),
    stop: !!(stop && (isCall ? price <= stop : price >= stop)),
    target: !!(target && (isCall ? price >= target : price <= target)),
  };
};


const getLiveBarVolume = (quote, interval) => {
  const intervalKey = String(interval || '').trim().toLowerCase();
  const aggregatedValue = quote?.volume_by_interval?.[intervalKey];
  if (aggregatedValue !== null && aggregatedValue !== undefined && aggregatedValue !== '') {
    const aggregatedVolume = Number(aggregatedValue);
    if (Number.isFinite(aggregatedVolume)) return aggregatedVolume;
  }

  if (
    quote?.volume_type === 'bar'
    && String(quote.volume_interval || '').toLowerCase() === intervalKey
  ) {
    const volume = Number(quote.volume);
    return Number.isFinite(volume) ? volume : null;
  }

  return null;
};

const getPriceDirection = (change) => {
  if (change === null || change === undefined || change === '') return 'unknown';
  const numericChange = Number(change);
  if (!Number.isFinite(numericChange)) return 'unknown';
  return numericChange > 0 ? 'buyers' : numericChange < 0 ? 'sellers' : 'neutral';
};

const getIntervalMilliseconds = (interval) => {
  const match = String(interval || '').toLowerCase().match(/^(\d+)(m|h|d)$/);
  if (!match) return null;
  const amount = Number(match[1]);
  const unitMilliseconds = { m: 60_000, h: 3_600_000, d: 86_400_000 };
  return amount > 0 ? amount * unitMilliseconds[match[2]] : null;
};

const getNewYorkDateKey = (timestamp) => {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

const liveVolumeMatchesLastBar = (bars, liveTime, interval) => {
  if (!bars?.length || !liveTime) return false;
  const lastBarTimestamp = bars[bars.length - 1]?.time;
  if (interval === '1d') return getNewYorkDateKey(lastBarTimestamp) === getNewYorkDateKey(liveTime);
  const lastBarTime = new Date(lastBarTimestamp).getTime();
  const quoteTime = new Date(liveTime).getTime();
  const intervalMilliseconds = getIntervalMilliseconds(interval);
  if (!Number.isFinite(lastBarTime) || !Number.isFinite(quoteTime) || !intervalMilliseconds) return false;
  const elapsed = quoteTime - lastBarTime;
  return elapsed >= 0 && elapsed < intervalMilliseconds;
};

const formatVolumeTimestamp = (value) => {
  if (!value) return 'Time unavailable';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return 'Time unavailable';
  const date = dt.toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).replace(/^Sep\b/, 'Sept');
  const time = dt.toLocaleTimeString('en-US', {
    timeZone: 'America/New_York',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
  return `${date} · ${time} ET`;
};

const toVolumeBars = (candles) => {
  if (!Array.isArray(candles) || candles.length === 0) return [];
  const normalized = candles.map((candle) => {
    const volume = Number(candle?.volume);
    const open = Number(candle?.open);
    const close = Number(candle?.close);
    return {
      volume: Number.isFinite(volume) && volume > 0 ? volume : 0,
      direction: Number.isFinite(open) && Number.isFinite(close)
        ? close > open ? 'buyers' : close < open ? 'sellers' : 'neutral'
        : 'unknown',
      time: candle?.time || null,
    };
  });

  const nonZero = normalized.filter((bar) => bar.volume > 0);
  const source = nonZero.length >= 5 ? nonZero : normalized;
  return source.slice(-10);
};

const VolumeSparkline = ({ scan, tableRootRef, liveQuote }) => {
  const chartScan = React.useMemo(() => ({
    id: scan?.id,
    period: scan?.period,
    interval: scan?.interval,
    include_extended_hours: scan?.include_extended_hours === true,
  }), [scan?.id, scan?.period, scan?.interval, scan?.include_extended_hours]);
  const expectedInterval = normalizeSignalChartInterval(scan?.interval);
  const expectedPeriod = String(scan?.period || '').trim();
  const cacheKey = JSON.stringify([scan?.id, expectedPeriod, expectedInterval, scan?.include_extended_hours === true]);
  const cachedChart = getCachedSignalChartData(chartScan);
  const [bars, setBars] = useState(() => cachedChart ? toVolumeBars(cachedChart.candles || []) : null);
  const [loadedCacheKey, setLoadedCacheKey] = useState(() => cachedChart ? cacheKey : null);
  const [loading, setLoading] = useState(!cachedChart);
  const [loadError, setLoadError] = useState(null);
  const [loadErrorKey, setLoadErrorKey] = useState(null);
  const hostRef = React.useRef(null);
  const liveVolume = getLiveBarVolume(liveQuote, expectedInterval);
  const volumeIsLive = liveQuote?.volume_is_live === true;

  useEffect(() => {
    let cancelled = false;

    const applyBars = (nextBars) => {
      if (cancelled) return;
      setBars(nextBars);
      setLoadedCacheKey(cacheKey);
      setLoading(false);
      setLoadError(null);
      setLoadErrorKey(null);
    };

    const unsubscribe = subscribeSignalChartData(chartScan, (chart) => {
      applyBars(toVolumeBars(chart.candles || []));
    });

    const loadBars = async () => {
      const existingChart = getCachedSignalChartData(chartScan);
      if (existingChart) {
        applyBars(toVolumeBars(existingChart.candles || []));
        return;
      }

      try {
        setLoading(true);
        const chart = await getSignalChartData(chartScan);
        applyBars(toVolumeBars(chart.candles || []));
      } catch (err) {
        if (!cancelled) {
          setBars([]);
          setLoadedCacheKey(cacheKey);
          setLoading(false);
          setLoadError(err.message || 'Failed to load matching volume bars.');
          setLoadErrorKey(cacheKey);
          console.error(`Failed to load volume bars for scan ${scan.id}:`, err);
        }
      }
    };

    const rootElement = tableRootRef?.current || null;
    if (!hostRef.current || !('IntersectionObserver' in window) || !rootElement) {
      loadBars();
      return () => {
        cancelled = true;
        unsubscribe();
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadBars();
          observer.disconnect();
        }
      },
      { root: rootElement, rootMargin: '120px' }
    );

    observer.observe(hostRef.current);

    return () => {
      cancelled = true;
      observer.disconnect();
      unsubscribe();
    };
  }, [cacheKey, chartScan, expectedInterval, expectedPeriod, scan.id, tableRootRef]);

  const currentBars = loadedCacheKey === cacheKey ? bars : null;
  const liveTime = liveQuote?.source_timestamp || liveQuote?.timestamp || null;
  const hasLiveVolume = liveVolume !== null
    && liveVolume >= 0
    && liveVolumeMatchesLastBar(currentBars, liveTime, expectedInterval);
  const displayBars = React.useMemo(() => {
    if (!currentBars?.length || !hasLiveVolume) return currentBars || [];
    return [
      ...currentBars.slice(0, Math.max(0, currentBars.length - 1)),
      {
        volume: liveVolume,
        direction: getPriceDirection(liveQuote?.change_percent),
        time: liveTime,
      },
    ];
  }, [currentBars, hasLiveVolume, liveVolume, liveQuote?.change_percent, liveTime]);

  const volumeValues = displayBars.map((bar) => Number(bar?.volume) || 0);
  const minVolume = volumeValues.length ? Math.min(...volumeValues) : 0;
  const maxVolume = volumeValues.length ? Math.max(...volumeValues) : 0;
  const volumeRange = Math.max(0, maxVolume - minVolume);

  return (
    <div
      ref={hostRef}
      className="flex flex-col items-start gap-1"
      title={`Volume for scan period ${expectedPeriod || '-'} at ${expectedInterval || '-'} interval`}
    >
      <div className="flex items-end gap-[2px] h-8 w-[4.8rem]">
        {loading && loadedCacheKey !== cacheKey ? (
          <span className="text-[10px] text-gray-400">⏳</span>
        ) : loadErrorKey === cacheKey ? (
          <span className="text-[10px] text-red-500" title={loadError}>Unavailable</span>
        ) : displayBars && displayBars.length > 0 ? (
          displayBars.map((bar, index) => {
            const volume = Number(bar?.volume) || 0;
            const timeLabel = formatVolumeTimestamp(bar?.time);
            // Local range scaling makes small differences between nearby bars easier to spot.
            const normalized = volumeRange > 0 ? (volume - minVolume) / volumeRange : 0.5;
            const contrastBoost = Math.pow(Math.max(0, Math.min(1, normalized)), 0.72);
            const height = Math.round(20 + contrastBoost * 80);
            const directionClass = bar?.direction === 'buyers'
              ? 'bg-green-500/85 hover:bg-green-600'
              : bar?.direction === 'sellers'
                ? 'bg-red-500/85 hover:bg-red-600'
                : 'bg-gray-400/75 hover:bg-gray-500';
            return (
              <span key={`${scan.id}-vol-${index}`} className="relative group flex items-end h-full">
                <span
                  className={`w-[3px] cursor-help ${directionClass}`}
                  style={{ height: `${height}%` }}
                />
                <span className="pointer-events-none absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block z-30">
                  <span className="block min-w-[185px] rounded-md border border-gray-200 bg-white px-3 py-2 text-xs leading-5 text-gray-800 shadow-lg">
                    <span className="block font-semibold">
                      {index === displayBars.length - 1 && hasLiveVolume
                        ? (volumeIsLive ? 'Live Volume' : 'Last Known Volume')
                        : `Bar ${displayBars.length - index}`}
                    </span>
                    <span className="block">Volume: {formatVolume(volume)}</span>
                    <span className="block">Time: {timeLabel}</span>
                  </span>
                </span>
              </span>
            );
          })
        ) : (
          <span className="text-[10px] text-gray-400">-</span>
        )}
      </div>
      <div className="text-[10px] leading-tight text-gray-400">
        {displayBars && displayBars.length > 0 ? formatVolume(displayBars[displayBars.length - 1]?.volume) : 'No volume'}
      </div>
    </div>
  );
};

const formatLivePriceTimestamp = (value) => {
  if (!value) return null;
  const timestamp = new Date(value);
  if (Number.isNaN(timestamp.getTime())) return null;
  const timeParts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).formatToParts(timestamp).reduce((parts, part) => {
    parts[part.type] = part.value;
    return parts;
  }, {});
  const dateParts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: '2-digit',
    month: 'long',
    day: 'numeric',
  }).formatToParts(timestamp).reduce((parts, part) => {
    parts[part.type] = part.value;
    return parts;
  }, {});
  const monthLabel = dateParts.month === 'September' ? 'Sept' : dateParts.month.slice(0, 3);

  return {
    date: `${monthLabel} ${dateParts.day}, ${dateParts.year}`,
    clock: `${timeParts.hour}:${timeParts.minute}:${timeParts.second} ${timeParts.dayPeriod}`,
    epoch: timestamp.getTime(),
  };
};

const LivePriceAge = ({ timestamp }) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => subscribeToLivePriceClock(setNow), []);

  return <>{formatLivePriceAge(timestamp.epoch, now)}</>;
};

const ScannedAge = ({ timestamp }) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => subscribeToLivePriceClock(setNow), []);

  return <>{formatScannedAge(timestamp, now)}</>;
};

// CSS for flashing animation - text and cells
const flashingStyles = `
  @keyframes quote-time-flash {
    0% {
      background-color: #fef3c7;
      color: #92400e;
    }
    100% {
      background-color: transparent;
      color: inherit;
    }
  }

  .quote-time-flash {
    animation: quote-time-flash 900ms ease-out;
  }

  @keyframes flash-green-text {
    0%, 100% {
      color: #10b981;
      text-shadow: 0 0 8px #10b981;
    }
    50% {
      color: #34d399;
      text-shadow: 0 0 16px #34d399;
    }
  }
  
  @keyframes flash-blue-text {
    0%, 100% {
      color: #3b82f6;
      text-shadow: 0 0 8px #3b82f6;
    }
    50% {
      color: #60a5fa;
      text-shadow: 0 0 16px #60a5fa;
    }
  }
  
  @keyframes flash-dot {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
      box-shadow: 0 0 3px 0 var(--flash-dot-color);
    }
    50% {
      opacity: 0.45;
      transform: scale(0.9);
      box-shadow: 0 0 0 0 var(--flash-dot-color);
    }
  }

  .flash-dot {
    display: inline-block;
    width: 0.625rem;
    height: 0.625rem;
    margin-left: 0.375rem;
    flex: none;
    border-radius: 9999px;
    background-color: var(--flash-dot-color);
    animation: flash-dot 1s infinite;
  }

  .flash-dot-green { --flash-dot-color: #10b981; }
  .flash-dot-red { --flash-dot-color: #dc2626; }
  
  .flash-green-text {
    animation: flash-green-text 1s infinite;
    font-weight: bold;
  }
  
  .flash-blue-text {
    animation: flash-blue-text 1s infinite;
    font-weight: bold;
  }

  .table-scrollbars {
    scrollbar-width: auto;
    scrollbar-color: #9ca3af #f3f4f6;
  }

  .table-scrollbars::-webkit-scrollbar {
    width: 12px;
    height: 12px;
  }

  .table-scrollbars::-webkit-scrollbar-track {
    background: #f3f4f6;
  }

  .table-scrollbars::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 9999px;
    border: 2px solid #f3f4f6;
  }

  .table-scrollbars::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
  }
`;


const ScanResultsTable = ({
  onLogout,
  isAdmin,
  onShowAdmin,
  onShowEarnings,
  onShowBacktest,
  onShowScanner,
}) => {
  const { t } = useTranslation();
  const [scanResults, setScanResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedRows, setSelectedRows] = useState(new Set()); // Track selected checkboxes
  const [livePrices, setLivePrices] = useState({}); // Store live prices by symbol
  const [wsConnected, setWsConnected] = useState(false);
  const [signalChartScan, setSignalChartScan] = useState(null);
  const [editingRows, setEditingRows] = useState({}); // Track edited values for executed and profit
  const [savingRows, setSavingRows] = useState(new Set()); // Track which rows are being saved
  const [isMarketPredictionSelected, setIsMarketPredictionSelected] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(500);
  const [totalCount, setTotalCount] = useState(0);
  const tableScrollRef = React.useRef(null);
  const mobileResultsRef = React.useRef(null);
  
  // WebSocket reconnection state
  const [wsReconnectAttempt, setWsReconnectAttempt] = useState(0);
  const wsRef = React.useRef(null);
  const wsReconnectAttemptRef = React.useRef(0);
  const wsReconnectTimerRef = React.useRef(null);
  
  const [filters, setFilters] = useState({
    symbol: '',
    status: '',
    has_signal: '',
    signal_type: '', // New filter for Put/Call
  });
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  // All active symbols for this user, used for filter suggestions and live-quote subscriptions
  const [availableSymbols, setAvailableSymbols] = useState([]);
  const visibleSymbols = React.useMemo(
    () => [...new Set(scanResults.map(scan => String(scan?.symbol || '').trim().toUpperCase()).filter(Boolean))].sort(),
    [scanResults]
  );
  const visibleSymbolsRef = React.useRef(visibleSymbols);
  visibleSymbolsRef.current = visibleSymbols;
  const wsSymbols = React.useMemo(
    () => buildLiveQuoteSubscription(visibleSymbols, availableSymbols),
    [visibleSymbols, availableSymbols]
  );
  const wsSymbolsKey = wsSymbols.join(',');

  // Add the flashing styles to the document
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = flashingStyles;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);


  // Sync filters from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlFilters = {
      symbol: params.get('symbol') || '',
      status: params.get('status') || '',
      has_signal: params.get('has_signal') || '',
      signal_type: params.get('signal_type') || '',
    };
    
    const urlPage = parseInt(params.get('page')) || 1;
    const urlPageSize = parseInt(params.get('pageSize')) || 500;
    
    setFilters(urlFilters);
    setPage(urlPage);
    setPageSize(urlPageSize);
  }, []);

  // Update URL when filters or pagination change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.symbol) params.set('symbol', filters.symbol);
    if (filters.status) params.set('status', filters.status);
    if (filters.has_signal) params.set('has_signal', filters.has_signal);
    if (filters.signal_type) params.set('signal_type', filters.signal_type);
    if (page > 1) params.set('page', page.toString());
    if (pageSize !== 500) params.set('pageSize', pageSize.toString());
    
    const newUrl = params.toString() ? `?${params.toString()}` : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }, [filters, page, pageSize]);

  const openSignalChartModal = (scan, event) => {
    if (event) event.stopPropagation();
    if (!scan?.has_signal || !scan?.signal_type) {
      return;
    }
    setSignalChartScan(scan);
  };

  const closeSignalChartModal = () => {
    setSignalChartScan(null);
  };

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sorted results
  const getSortedResults = (results) => {
    // First, add calculated gain_percent to each result
    const resultsWithGain = results.map(scan => {
      let gain_percent = null;
      if (scan.entry_price && scan.target_price) {
        // For CALL: profit = target > entry → positive %
        // For PUT:  profit = price falls to target (target < entry) → flip sign so winning PUT shows positive
        gain_percent = scan.signal_type === 'PUT'
          ? ((scan.entry_price - scan.target_price) / scan.entry_price) * 100
          : ((scan.target_price - scan.entry_price) / scan.entry_price) * 100;
      }
      return { ...scan, gain_percent };
    });

    if (!sortConfig.key) return resultsWithGain;

    const sorted = [...resultsWithGain].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Handle live prices for sorting
      if (sortConfig.key === 'live_price') {
        aVal = getLivePriceValue(
          getLiveQuoteForSymbol(livePrices, a.symbol)
        ) || 0;
        bVal = getLivePriceValue(
          getLiveQuoteForSymbol(livePrices, b.symbol)
        ) || 0;
      }

      if (sortConfig.key.startsWith('flash_')) {
        const part = sortConfig.key.slice(6);
        aVal = getFlashState(a, getLiveQuoteForSymbol(livePrices, a.symbol))[part] ? 1 : 0;
        bVal = getFlashState(b, getLiveQuoteForSymbol(livePrices, b.symbol))[part] ? 1 : 0;
      }

      // Handle null/undefined values
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Boolean comparison (for executed field)
      if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
        // false (0) before true (1) for ascending, opposite for descending
        const aNum = aVal ? 1 : 0;
        const bNum = bVal ? 1 : 0;
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Numeric comparison
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // String comparison
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  // Sort icon component
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <span className="text-gray-400 ml-1">⇅</span>;
    }
    return sortConfig.direction === 'asc' 
      ? <span className="text-blue-600 ml-1">↑</span>
      : <span className="text-blue-600 ml-1">↓</span>;
  };

  // WebSocket connection for live prices with auto-reconnect
  useEffect(() => {
    if (wsSymbols.length === 0) {
      // Clean up WebSocket if no results
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setWsConnected(false);
      return;
    }

    const symbols = wsSymbols;

    // Check if symbols changed
    const currentSymbols = wsRef.current?.symbols || [];
    const symbolsChanged = JSON.stringify([...symbols].sort()) !== JSON.stringify([...currentSymbols].sort());
    
    // If WebSocket exists and symbols haven't changed, just update subscription
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && !symbolsChanged) {
      return; // No need to reconnect
    }
    
    // If symbols changed and connection is open, send new subscription
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && symbolsChanged) {
      wsRef.current.symbols = symbols;
      wsRef.current.send(JSON.stringify({
        symbols: symbols,
        interval: 5
      }));
      return;
    }

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
    }
    if (wsReconnectTimerRef.current) {
      clearTimeout(wsReconnectTimerRef.current);
      wsReconnectTimerRef.current = null;
    }

    // WebSocket URL
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://') + '/api/ws/prices';

    const applyQuotes = (quotes) => {
      if (!Array.isArray(quotes) || quotes.length === 0) return;

      const mappedQuotes = {};
      quotes.forEach((quote) => {
        const normalizedSymbol = normalizeQuoteSymbolKey(quote?.symbol);
        if (!normalizedSymbol) return;
        mappedQuotes[normalizedSymbol] = {
          price: quote.price,
          change_percent: quote.change_percent,
          volume: quote.volume,
          volume_type: quote.volume_type,
          volume_interval: quote.volume_interval,
          volume_by_interval: quote.volume_by_interval,
          volume_is_live: quote.volume_is_live,
          timestamp: quote.timestamp,
          source_timestamp: quote.source_timestamp,
          source_delay_seconds: quote.source_delay_seconds,
          max_quote_age_seconds: quote.max_quote_age_seconds,
          is_delayed: quote.is_delayed,
          price_is_live: quote.price_is_live,
          price_time_kind: quote.price_time_kind,
          price_interval: quote.price_interval,
          source: quote.source,
        };
      });

      if (Object.keys(mappedQuotes).length === 0) return;

      setLivePrices((prev) => {
        const next = { ...prev };
        Object.entries(mappedQuotes).forEach(([symbol, quote]) => {
          const previousRevision = Number(prev[symbol]?._timeRevision) || 0;
          const receivedTimestamp = quote.timestamp !== undefined || quote.source_timestamp !== undefined;
          next[symbol] = {
            ...quote,
            _timeRevision: previousRevision + (receivedTimestamp ? 1 : 0),
          };
        });
        return next;
      });
    };

    const requestQuoteSnapshot = async (targetSymbols) => {
      const cleanSymbols = [...new Set((targetSymbols || []).map((s) => String(s || '').trim().toUpperCase()).filter(Boolean))];
      if (cleanSymbols.length === 0) return;

      try {
        const response = await axios.get(`${API}/ws/prices/quote-snapshot`, {
          params: {
            symbols: cleanSymbols.join(','),
            max_symbols: Math.min(cleanSymbols.length, 1000),
          },
        });
        applyQuotes(response.data?.quotes || []);
      } catch (err) {
        console.error('Quote snapshot fallback failed:', err);
      }
    };
    
    const connectWebSocket = () => {
      const ws = new WebSocket(wsUrl);
      ws.symbols = symbols; // Store symbols on WebSocket object
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('✅ WebSocket connected');
        setWsConnected(true);
        setWsReconnectAttempt(0); // Reset reconnection attempts
        wsReconnectAttemptRef.current = 0;
        
        // Subscribe to symbols; snapshot will be requested on the 'subscribed' ack.
        ws.send(JSON.stringify({
          symbols: symbols,
          interval: 5
        }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'error') {
            console.error('WebSocket subscription error:', data.message);
            setWsConnected(false);
            return;
          }

          if (data.type === 'ping') {
            ws.send(JSON.stringify({ type: 'pong' }));
            return;
          }

          if (data.type === 'subscribed') {
            // Catch up only the visible rows; the shared poll loop fills in the rest.
            const subscribed = new Set(Array.isArray(data.symbols) ? data.symbols : symbols);
            requestQuoteSnapshot(visibleSymbolsRef.current.filter((symbol) => subscribed.has(symbol)));
            return;
          }
          
          if (data.type === 'price_update') {
            const normalizedSymbol = normalizeQuoteSymbolKey(data.symbol);
            if (!normalizedSymbol) return;
            const nextQuote = {
              price: data.price,
              change_percent: data.change_percent,
              volume: data.volume,
              volume_type: data.volume_type,
              volume_interval: data.volume_interval,
              volume_by_interval: data.volume_by_interval,
              volume_is_live: data.volume_is_live,
              timestamp: data.timestamp,
              source_timestamp: data.source_timestamp,
              source_delay_seconds: data.source_delay_seconds,
              max_quote_age_seconds: data.max_quote_age_seconds,
              is_delayed: data.is_delayed,
              price_is_live: data.price_is_live,
              price_time_kind: data.price_time_kind,
              price_interval: data.price_interval,
              source: data.source
            };
            setLivePrices((prev) => {
              const previousRevision = Number(prev[normalizedSymbol]?._timeRevision) || 0;
              const receivedTimestamp = data.timestamp !== undefined || data.source_timestamp !== undefined;
              return {
                ...prev,
                [normalizedSymbol]: {
                  ...nextQuote,
                  _timeRevision: previousRevision + (receivedTimestamp ? 1 : 0),
                },
              };
            });
            return;
          }

          if (data.type === 'price_batch_update' && Array.isArray(data.quotes)) {
            applyQuotes(data.quotes);
            return;
          }

          if (data.type === 'quote_pull_update' && Array.isArray(data.quotes)) {
            setLivePrices((prev) => {
              const next = { ...prev };
              data.quotes.forEach((quote) => {
                const normalizedSymbol = normalizeQuoteSymbolKey(quote?.symbol);
                if (!normalizedSymbol || !next[normalizedSymbol]) return;
                const previousRevision = Number(next[normalizedSymbol]._timeRevision) || 0;
                const receivedTimestamp = quote.timestamp !== undefined || quote.source_timestamp !== undefined;
                next[normalizedSymbol] = {
                  ...next[normalizedSymbol],
                  ...quote,
                  _timeRevision: previousRevision + (receivedTimestamp ? 1 : 0),
                };
              });
              return next;
            });
          }
        } catch (err) {
          console.error('WebSocket message error:', err);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setWsConnected(false);
        
        // Auto-reconnect with exponential backoff and jitter
        if (symbols.length > 0) {
          const attempt = wsReconnectAttemptRef.current;
          const baseDelay = Math.min(1000 * Math.pow(2, attempt), 30000); // Max 30 seconds
          const jitter = Math.floor(Math.random() * Math.max(250, Math.floor(baseDelay * 0.2)));
          const delay = baseDelay + jitter;
          
          console.log(`Reconnecting WebSocket in ${delay/1000}s (attempt ${attempt + 1})...`);

          wsReconnectAttemptRef.current = attempt + 1;
          setWsReconnectAttempt(wsReconnectAttemptRef.current);

          wsReconnectTimerRef.current = setTimeout(() => {
            connectWebSocket();
          }, delay);
        }
      };
    };

    connectWebSocket();

    // Cleanup on unmount
    return () => {
      if (wsReconnectTimerRef.current) {
        clearTimeout(wsReconnectTimerRef.current);
        wsReconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [wsSymbolsKey, BACKEND_URL]);

  const searchAbortRef = React.useRef(null);

  // Expand comma-separated terms so company names also resolve to their symbols
  const buildSymbolQuery = (value) => {
    const terms = value.split(',').map(t => t.trim()).filter(Boolean);
    const result = new Set();
    const available = availableSymbols.length ? new Set(availableSymbols) : null;
    terms.forEach(term => {
      result.add(term);
      if (term.length < 3) return;
      const lower = term.toLowerCase();
      SYMBOL_DATA.forEach(item => {
        if (item.name.toLowerCase().includes(lower) && (!available || available.has(item.symbol))) {
          result.add(item.symbol);
        }
      });
    });
    return [...result].join(',');
  };

  const fetchScanResults = async (options) => {
    // Silent refreshes (auto-verify) keep the table in place instead of showing the loading state
    const silent = options?.silent === true;
    // Cancel any in-flight search so stale responses never overwrite newer ones
    if (searchAbortRef.current) searchAbortRef.current.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;

    if (!silent) {
      setLoading(true);
      setError('');
    }

    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      // Pagination parameters
      params.append('limit', pageSize.toString());
      params.append('offset', ((page - 1) * pageSize).toString());
      
      // Filter parameters
      const symbolQuery = buildSymbolQuery(filters.symbol);
      if (symbolQuery) params.append('symbol', symbolQuery);
      if (filters.status) params.append('status', filters.status);
      if (filters.has_signal) params.append('has_signal', filters.has_signal);
      if (filters.signal_type) params.append('signal_type', filters.signal_type);

      const response = await axios.get(`${API}/scanner/search?${params}`, {
        signal: controller.signal,
        headers: { Authorization: `Bearer ${token}` },
      });

      const fetchedResults = response.data.results || [];
      setScanResults(fetchedResults);
      setTotalCount(response.data.total_count || 0);
      return fetchedResults;
    } catch (err) {
      if (isRequestAborted(err)) {
        return;
      }

      setError((Array.isArray(err.response?.data?.detail) ? err.response.data.detail.map(e => e.msg || String(e)).join('; ') : err.response?.data?.detail) || 'Failed to fetch scan results');
      if (err.response?.status === 401) {
        onLogout();
      }
    } finally {
      if (searchAbortRef.current === controller) {
        searchAbortRef.current = null;
        setLoading(false);
      }
    }
  };

  const [symbolDropdownOpen, setSymbolDropdownOpen] = useState(false);
  const [symbolHighlight, setSymbolHighlight] = useState(-1);
  const STATUS_OPTIONS = ['PENDING', 'ENTER_NOW', 'TARGET_HIT', 'STOPPED', 'EXPIRED'];
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const selectedStatuses = filters.status ? filters.status.split(',').filter(Boolean) : [];
  const toggleStatus = (opt) => {
    setFilters(prev => {
      const current = prev.status ? prev.status.split(',').filter(Boolean) : [];
      const next = current.includes(opt) ? current.filter(s => s !== opt) : [...current, opt];
      return { ...prev, status: next.join(',') };
    });
  };
  const symbolPrefix = filters.symbol.split(',').pop().trim();
  const symbolPrefixUpper = symbolPrefix.toUpperCase();
  const symbolSuggestions = (() => {
    if (!symbolPrefix) return availableSymbols.slice(0, 50);
    const byPrefix = availableSymbols.filter(sym => sym.startsWith(symbolPrefixUpper));
    const byName = symbolPrefix.length >= 2
      ? availableSymbols.filter(sym => !sym.startsWith(symbolPrefixUpper)
        && (COMPANY_NAMES.get(sym) || '').toLowerCase().includes(symbolPrefix.toLowerCase()))
      : [];
    return [...byPrefix, ...byName].slice(0, 50);
  })();
  const selectSymbol = (sym) => {
    setFilters(prev => {
      if (!sym) return { ...prev, symbol: '' };
      const parts = prev.symbol.split(',');
      parts[parts.length - 1] = sym;
      return { ...prev, symbol: parts.map(p => p.trim()).join(',') + ',' };
    });
    setSymbolDropdownOpen(false);
    setSymbolHighlight(-1);
  };
  const fetchAvailableSymbols = React.useCallback(() => {
    const token = localStorage.getItem('token');
    return axios.get(`${API}/scanner/symbols`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const next = res.data.symbols || [];
        // Keep the same array when unchanged so the live-quote subscription isn't rebuilt
        setAvailableSymbols(prev => (prev.join(',') === next.join(',') ? prev : next));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchAvailableSymbols();
  }, [fetchAvailableSymbols]);

  // Reset to first page when filters change
  const filtersMountedRef = React.useRef(false);
  useEffect(() => {
    if (!filtersMountedRef.current) {
      filtersMountedRef.current = true;
      return;
    }
    setPage(1);
  }, [filters.symbol, filters.status, filters.signal_type]);

  // Search as you type: refetch from the database (debounced) when filters, page or pageSize change
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchScanResults();
    }, 300);
    return () => clearTimeout(timer);
  }, [page, pageSize, filters.symbol, filters.status, filters.signal_type]);

  // State for tracking which scan is being verified
  const [verifyingScans, setVerifyingScans] = useState(new Set());
  const [openActionsMenu, setOpenActionsMenu] = useState(null);

  // Delete confirmation dialog: { type: 'single' | 'bulk' | 'expired', ids: number[] }
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const closeDeleteDialog = () => {
    if (deleteBusy) return;
    setDeleteDialog(null);
    setDeleteError('');
  };

  const getErrorText = (err, fallback) => (
    (Array.isArray(err.response?.data?.detail)
      ? err.response.data.detail.map(e => e.msg || String(e)).join('; ')
      : err.response?.data?.detail) || err.message || fallback
  );

  const handleRemove = (scanId, e) => {
    e.stopPropagation(); // Prevent row click when clicking remove button
    setDeleteError('');
    setDeleteDialog({ type: 'single', ids: [scanId] });
  };

  const confirmDelete = async () => {
    if (!deleteDialog) return;
    setDeleteBusy(true);
    setDeleteError('');
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (deleteDialog.type === 'single') {
        await axios.delete(`${API}/scanner/${deleteDialog.ids[0]}`, { headers });
        setScanResults(prev => prev.filter(scan => scan.id !== deleteDialog.ids[0]));
      } else if (deleteDialog.type === 'bulk') {
        await axios.post(`${API}/scanner/delete-multiple`, deleteDialog.ids, {
          headers: { ...headers, 'Content-Type': 'application/json' },
        });
        setSelectedRows(new Set());
        await fetchScanResults();
      } else {
        await axios.post(`${API}/scanner/archive-expired`, { scan_ids: deleteDialog.ids }, { headers });
        await fetchScanResults();
      }
      fetchAvailableSymbols();
      setDeleteDialog(null);
    } catch (err) {
      setDeleteError(getErrorText(err, 'Failed to delete'));
      if (err.response?.status === 401) {
        onLogout();
      }
    } finally {
      setDeleteBusy(false);
    }
  };

  // Verify Status dialog: { items: scan[], results: { [id]: { state, from, to, message } }, running, finished }
  const [verifyDialog, setVerifyDialog] = useState(null);

  const handleVerifyDisplayedInParallel = () => {
    if (scanResults.length === 0) return;
    setVerifyDialog({ items: [...scanResults], results: {}, running: false, finished: false });
  };

  // Status link: open the same modal for a single row
  const handleVerifyOne = (scanId, e) => {
    e?.stopPropagation();
    const scan = scanResults.find(s => s.id === scanId);
    if (!scan) return;
    setVerifyDialog({ items: [scan], results: {}, running: false, finished: false });
  };

  // Automatic verification (per-user setting stored in the database; default ON, every 60 seconds)
  const [autoVerify, setAutoVerify] = useState({ enabled: true, interval_seconds: 60 });
  const [autoVerifyLoaded, setAutoVerifyLoaded] = useState(false);
  const [autoVerifyRunning, setAutoVerifyRunning] = useState(false);
  const [autoVerifyLastRun, setAutoVerifyLastRun] = useState(null); // { time, message, tone }
  const [autoVerifySave, setAutoVerifySave] = useState(null); // { state: 'saving'|'ok'|'error', message }
  const [autoVerifyCustomTime, setAutoVerifyCustomTime] = useState({ hours: '0', minutes: '1', seconds: '0' });
  const [autoVerifyCustomError, setAutoVerifyCustomError] = useState('');
  const autoVerifySaveTimerRef = React.useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [autoVerifyDetails, setAutoVerifyDetails] = useState(null);
  const autoVerifyContextRef = React.useRef({});
  autoVerifyContextRef.current = {
    scanResults,
    busy: verifyingScans.size > 0 || loading,
    refresh: fetchScanResults,
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/scanner/auto-verify-settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setAutoVerify({
        enabled: res.data.enabled !== false,
        interval_seconds: res.data.interval_seconds || 60,
      }))
      .catch(err => setAutoVerifySave({
        state: 'error',
        message: getErrorText(err, 'Failed to load auto-verify settings'),
      }))
      .finally(() => setAutoVerifyLoaded(true));
  }, []);

  useEffect(() => {
    const seconds = autoVerify.interval_seconds;
    setAutoVerifyCustomTime({
      hours: String(Math.floor(seconds / 3600)),
      minutes: String(Math.floor(seconds / 60) % 60),
      seconds: String(seconds % 60),
    });
    setAutoVerifyCustomError('');
  }, [autoVerify.interval_seconds]);

  const updateAutoVerify = async (changes) => {
    const previous = autoVerify;
    const next = { ...autoVerify, ...changes };
    setAutoVerify(next);
    clearTimeout(autoVerifySaveTimerRef.current);
    setAutoVerifySave({ state: 'saving', message: 'Saving...' });
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/scanner/auto-verify-settings`, next, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      setAutoVerify(previous);
      setAutoVerifySave({ state: 'error', message: getErrorText(err, 'Failed to save auto-verify setting') });
      return;
    }
    setAutoVerifySave({ state: 'ok', message: 'Setting saved' });
    autoVerifySaveTimerRef.current = setTimeout(() => setAutoVerifySave(null), 3000);
  };

  const applyCustomAutoVerifyInterval = () => {
    let seconds;
    try {
      seconds = parseAutoVerifyInterval(
        autoVerifyCustomTime.hours, autoVerifyCustomTime.minutes, autoVerifyCustomTime.seconds
      );
    } catch (err) {
      setAutoVerifyCustomError(err.message);
      return;
    }
    setAutoVerifyCustomError('');
    updateAutoVerify({ interval_seconds: seconds });
  };

  useEffect(() => {
    if (!autoVerifyLoaded || !autoVerify.enabled) return undefined;

    const run = async () => {
      const { scanResults: rows, busy, refresh } = autoVerifyContextRef.current;
      if (document.hidden) return;
      if (busy) {
        setAutoVerifyLastRun({ time: new Date(), message: 'Skipped: table busy', tone: 'muted' });
        return;
      }
      // Verification runs on the server; this timer only refreshes visible results.
      setAutoVerifyRunning(true);
      try {
        const before = new Map(rows.map(scan => [scan.id, scan]));
        const after = await refresh({ silent: true });
        const now = new Date();
        setAutoVerifyLastRun({ time: now, message: 'Background results refreshed', tone: 'ok' });
        const changes = (after || [])
          .filter(scan => before.has(scan.id) && before.get(scan.id).status !== scan.status)
          .map(scan => ({
            id: scan.id,
            symbol: scan.symbol,
            signal_type: scan.signal_type,
            from: before.get(scan.id).status,
            to: scan.status,
            entry_price: scan.entry_price,
            stop_price: scan.stop_price,
            target_price: scan.target_price,
            status_datetime: scan.status_datetime,
          }));
        setNotifications(prev => addAutoVerifyNotification(prev, {
          id: now.getTime(), time: now, outcome: 'verified', checked: rows.length, changes,
        }));
      } catch (err) {
        if (err.response?.status === 401) onLogout();
        const now = new Date();
        const message = `Failed: ${getErrorText(err, 'Request failed')}`;
        setAutoVerifyLastRun({ time: now, message, tone: 'error' });
        setNotifications(prev => addAutoVerifyNotification(prev, {
          id: now.getTime(), time: now, outcome: 'error', message, checked: rows.length, changes: [],
        }));
      } finally {
        setAutoVerifyRunning(false);
      }
    };

    const timer = setInterval(run, autoVerify.interval_seconds * 1000);
    return () => clearInterval(timer);
  }, [autoVerifyLoaded, autoVerify.enabled, autoVerify.interval_seconds]);

  const closeVerifyDialog = () => {
    // Verification keeps running in the background; the table still updates when it finishes
    setVerifyDialog(null);
  };

  const removeFromVerifyDialog = (id) => {
    setVerifyDialog(prev => (prev ? { ...prev, items: prev.items.filter(s => s.id !== id) } : prev));
  };

  const confirmVerify = async () => {
    if (!verifyDialog || verifyDialog.running || verifyDialog.items.length === 0) return;

    const scansToVerify = verifyDialog.items;
    const scanIds = scansToVerify.map(scan => scan.id);
    const initial = {};
    scansToVerify.forEach(scan => { initial[scan.id] = { state: 'running', from: scan.status }; });
    setVerifyDialog(prev => ({ ...prev, results: initial, running: true, finished: false }));
    setError('');
    setVerifyingScans(prev => new Set([...prev, ...scanIds]));

    const token = localStorage.getItem('token');
    const updatedScans = new Map();

    await Promise.all(scansToVerify.map(async (scan) => {
      let result;
      try {
        const response = await axios.post(
          `${API}/scanner/verify`,
          { scan_result_id: scan.id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        updatedScans.set(scan.id, {
          status: response.data.status,
          status_datetime: response.data.status_datetime,
        });
        result = { state: 'ok', from: scan.status, to: response.data.status, message: response.data.reason };
      } catch (err) {
        result = { state: 'error', from: scan.status, message: getErrorText(err, 'Request failed') };
      }
      setVerifyDialog(prev => (prev ? { ...prev, results: { ...prev.results, [scan.id]: result } } : prev));
    }));

    setScanResults(prev => prev.map(scan => {
      const update = updatedScans.get(scan.id);
      return update ? { ...scan, ...update } : scan;
    }));
    setVerifyingScans(prev => {
      const next = new Set(prev);
      scanIds.forEach(scanId => next.delete(scanId));
      return next;
    });
    setVerifyDialog(prev => (prev ? { ...prev, running: false, finished: true } : prev));
  };

  // Archive expired positions: load every expired/invalid prediction (all pages) into the dialog
  const handleArchiveExpired = async () => {
    setDeleteError('');
    setDeleteDialog({ type: 'expired', ids: [], items: [], loadingItems: true });

    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ status: 'EXPIRED,INVALID', limit: '1000', offset: '0' });
      const response = await axios.get(`${API}/scanner/search?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const items = response.data.results || [];
      setDeleteDialog(prev => (prev && prev.type === 'expired'
        ? { ...prev, items, ids: items.map(s => s.id), loadingItems: false, totalCount: response.data.total_count || items.length }
        : prev));
    } catch (err) {
      setDeleteError(getErrorText(err, 'Failed to load expired positions'));
      setDeleteDialog(prev => (prev ? { ...prev, loadingItems: false } : prev));
      if (err.response?.status === 401) {
        onLogout();
      }
    }
  };

  const handleScanComplete = () => {
    fetchScanResults({ silent: true });
    fetchAvailableSymbols();
  };

  const handleScanNotification = ({ scanned, signals, existing_predictions = 0, findings = [], source = 'manual', name, errors = [], deferred = 0 }) => {
    const now = new Date();
    setNotifications(prev => [
      { id: `${now.getTime()}-${Math.random()}`, type: 'scan', time: now, scanned, signals, existing_predictions, findings, source, name, errors, deferred, read: false },
      ...prev,
    ].slice(0, 20));
  };

  const getStatusBadge = (status, scanId) => {
    const label = status
      ? status.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
      : 'N/A';
    const className = `text-xs ${status ? 'font-semibold' : ''} ${
      status === 'PENDING'
        ? 'text-yellow-800'
        : status === 'ENTER_NOW'
          ? 'text-green-800'
          : status === 'TARGET_HIT'
            ? 'text-blue-800'
            : status === 'STOPPED' || status === 'INVALID'
              ? 'text-red-800'
              : status === 'EXPIRED'
                ? 'text-gray-800'
                : 'text-gray-600'
    }`;
    
    return scanId ? (
      <button
        type="button"
        onClick={(event) => handleVerifyOne(scanId, event)}
        disabled={verifyingScans.has(scanId)}
        className={`${className} cursor-pointer border-0 bg-transparent p-0 underline underline-offset-2 disabled:cursor-wait`}
        title={verifyingScans.has(scanId) ? 'Verifying...' : 'Verify Status'}
      >
        {label}
      </button>
    ) : <span className={className}>{label}</span>;
  };

  // Checkbox selection handlers
  const areAllRowsSelected = scanResults.length > 0
    && scanResults.every(scan => selectedRows.has(scan.id));

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Select all visible rows
      const allIds = new Set(scanResults.map(scan => scan.id));
      setSelectedRows(allIds);
    } else {
      // Deselect all
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (scanId) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(scanId)) {
      newSelected.delete(scanId);
    } else {
      newSelected.add(scanId);
    }
    setSelectedRows(newSelected);
  };

  // Handle executed checkbox change (stores in local state, not saved until Save button clicked)
  const handleExecutedChange = (scanId, executed) => {
    setEditingRows(prev => ({
      ...prev,
      [scanId]: {
        ...prev[scanId],
        executed: executed
      }
    }));
  };

  // Handle profit input change (stores in local state, not saved until Save button clicked)
  const handleProfitChange = (scanId, profit) => {
    setEditingRows(prev => ({
      ...prev,
      [scanId]: {
        ...prev[scanId],
        profit: profit
      }
    }));
  };

  // Save execution details (executed status and profit)
  const handleSaveExecution = async (scanId, scan) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        alert('You must be logged in to save execution details');
        return;
      }

      // Get values from editing state or original scan values
      const editedData = editingRows[scanId] || {};
      const executed = editedData.executed !== undefined ? editedData.executed : scan.executed;
      const profitValue = editedData.profit !== undefined ? editedData.profit : scan.profit;

      // Validate profit is a number if provided
      let profitNumber = null;
      if (profitValue !== null && profitValue !== undefined && profitValue !== '') {
        profitNumber = parseFloat(profitValue);
        if (isNaN(profitNumber)) {
          alert('Please enter a valid number for profit');
          return;
        }
      }

      // Mark row as saving
      setSavingRows(prev => new Set(prev).add(scanId));

      // Update backend
      const response = await axios.put(
        `${API}/scanner/executed`,
        {
          scan_result_id: scanId,
          executed: executed,
          profit: profitNumber
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.status === 200) {
        // Update local state
        setScanResults(prevResults =>
          prevResults.map(s =>
            s.id === scanId
              ? { ...s, executed: executed, profit: profitNumber }
              : s
          )
        );
        
        // Clear editing state for this row
        setEditingRows(prev => {
          const newState = { ...prev };
          delete newState[scanId];
          return newState;
        });
        
        console.log(`✅ Saved execution details for scan ${scanId}`);
      }
    } catch (err) {
      console.error('Error saving execution details:', err);
      alert(`Error saving execution details: ${(Array.isArray(err.response?.data?.detail) ? err.response.data.detail.map(e => e.msg || String(e)).join('; ') : err.response?.data?.detail) || err.message}`);
    } finally {
      // Remove from saving state
      setSavingRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(scanId);
        return newSet;
      });
    }
  };

  // Bulk delete handler
  const handleBulkDelete = () => {
    if (selectedRows.size === 0) {
      alert('Please select at least one row to delete');
      return;
    }

    setDeleteError('');
    setDeleteDialog({ type: 'bulk', ids: Array.from(selectedRows) });
  };

  const removeFromDeleteDialog = (id) => {
    const remaining = deleteDialog.ids.filter(x => x !== id);
    if (remaining.length === 0 && deleteDialog.type !== 'expired') {
      setDeleteDialog(null);
      setDeleteError('');
    } else {
      setDeleteDialog({ ...deleteDialog, ids: remaining });
    }
  };

  const deleteDialogScans = deleteDialog
    ? (deleteDialog.items
        ? deleteDialog.items.filter(s => deleteDialog.ids.includes(s.id))
        : deleteDialog.ids.map(id => scanResults.find(s => s.id === id)).filter(Boolean))
    : [];
  const deleteDialogTitle = deleteDialog?.type === 'expired'
    ? 'Clean up expired & invalid predictions'
    : deleteDialog?.ids.length === 1
      ? 'Delete market prediction'
      : 'Delete market predictions';

  return (
    <div className="min-h-screen h-auto lg:h-screen bg-gray-100 max-w-full flex flex-col overflow-x-clip">
      <AppHeader
        title={null}
        isAdmin={isAdmin}
        onShowScanner={onShowScanner}
        onShowEarnings={onShowEarnings}
        onShowBacktest={onShowBacktest}
        onShowAdmin={onShowAdmin}
        onLogout={onLogout}
        notifications={notifications}
        onOpenNotification={(notification) => {
          setNotifications(prev => prev.map(n => (n.id === notification.id ? { ...n, read: true } : n)));
          setAutoVerifyDetails(notification);
        }}
        onClearNotifications={() => setNotifications([])}
      />
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row lg:overflow-hidden max-w-full">
        {/* Left Sidebar - Scanner Menu */}
        <ScannerMenu onScanComplete={handleScanComplete} onScanNotification={handleScanNotification} />

        {/* Main Content */}
        <div className="flex-1 w-full max-w-full min-w-0 lg:overflow-hidden flex flex-col lg:min-h-0">
          {/* Main Content Area */}
          <div className="mx-auto flex-1 w-full max-w-none min-w-0 px-0 py-3 sm:py-6 flex flex-col lg:mx-0 lg:p-6 lg:pt-3 lg:min-h-0 lg:overflow-hidden">
        <div className="contents lg:block lg:flex-none">
        <div className="mb-2 flex w-full items-center gap-2 border-b border-blue-100 pb-2 px-6 sm:mb-4 lg:px-0">
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
              {t('resistance_scanner_label')}
            </h2>
            <button
              type="button"
              onClick={() => setIsMarketPredictionSelected(selected => !selected)}
              aria-label={t('market_prediction')}
              aria-pressed={isMarketPredictionSelected}
              aria-controls="market-prediction-filters"
              className="ml-auto hidden h-9 w-9 shrink-0 items-center justify-center rounded text-gray-600 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 lg:inline-flex"
            >
              {isMarketPredictionSelected ? (
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                  <circle cx="11" cy="11" r="6" strokeWidth="2" />
                  <path d="m16 16 4 4" strokeWidth="2" strokeLinecap="round" />
                </svg>
              ) : (
                <svg className="h-5 w-5 rotate-180 transition-transform" viewBox="0 0 20 20" fill="none" stroke="currentColor" aria-hidden="true">
                  <path d="m5 7.5 5 5 5-5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          </div>
          {/* Filters */}
          <div
            id="market-prediction-filters"
            className={`sticky top-[var(--app-header-height)] z-40 mx-3 bg-white p-2 sm:p-4 rounded-lg border border-gray-200 shadow mb-3 max-w-full lg:mx-0 lg:mb-6 lg:border-0 lg:static lg:z-auto ${
              isMarketPredictionSelected ? 'lg:hidden' : ''
            }`}
          >
            <div className="grid grid-cols-3 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
              <div className="min-w-0">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  {t('symbol')}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={filters.symbol}
                    onChange={(e) => {
                      setFilters({ ...filters, symbol: e.target.value.toUpperCase() });
                      setSymbolDropdownOpen(true);
                      setSymbolHighlight(-1);
                    }}
                    onFocus={() => setSymbolDropdownOpen(true)}
                    onBlur={() => setTimeout(() => setSymbolDropdownOpen(false), 150)}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setSymbolDropdownOpen(true);
                        setSymbolHighlight(h => Math.min(h + 1, symbolSuggestions.length - 1));
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setSymbolHighlight(h => Math.max(h - 1, 0));
                      } else if (e.key === 'Enter' && symbolDropdownOpen && symbolHighlight >= 0) {
                        e.preventDefault();
                        selectSymbol(symbolSuggestions[symbolHighlight]);
                      } else if (e.key === 'Escape') {
                        setSymbolDropdownOpen(false);
                      }
                    }}
                    placeholder={t('search_by_symbol')}
                    autoComplete="off"
                    role="combobox"
                    aria-expanded={symbolDropdownOpen && symbolSuggestions.length > 0}
                    aria-autocomplete="list"
                    className="w-full min-w-0 px-2 sm:px-3 py-2 pr-8 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  {filters.symbol && (
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => selectSymbol('')}
                      aria-label="Clear symbol"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none"
                    >
                      ×
                    </button>
                  )}
                  {symbolDropdownOpen && symbolSuggestions.length > 0 && (
                    <ul
                      role="listbox"
                      className="absolute z-30 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1"
                    >
                      {symbolSuggestions.map((sym, idx) => (
                        <li
                          key={sym}
                          role="option"
                          aria-selected={idx === symbolHighlight}
                          ref={idx === symbolHighlight ? (el) => el && el.scrollIntoView({ block: 'nearest' }) : undefined}
                          onMouseDown={(e) => { e.preventDefault(); selectSymbol(sym); }}
                          onMouseEnter={() => setSymbolHighlight(idx)}
                          className={`px-3 py-1.5 text-sm cursor-pointer ${idx === symbolHighlight ? 'bg-blue-100 text-blue-900' : 'text-gray-700 hover:bg-gray-50'}`}
                        >
                          {symbolPrefix && sym.startsWith(symbolPrefixUpper) ? (
                            <><span className="font-bold">{sym.slice(0, symbolPrefix.length)}</span>{sym.slice(symbolPrefix.length)}</>
                          ) : sym}
                          {COMPANY_NAMES.get(sym) && (
                            <span className="ml-2 text-xs text-gray-400">{COMPANY_NAMES.get(sym)}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="min-w-0">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Put/Call
                </label>
                <select
                  value={filters.signal_type}
                  onChange={(e) => setFilters({ ...filters, signal_type: e.target.value })}
                  className="w-full px-2 sm:px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('all')}</option>
                  <option value="CALL">Call</option>
                  <option value="PUT">Put</option>
                </select>
              </div>

              <div className="min-w-0">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  {t('status')}
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setStatusDropdownOpen(o => !o)}
                    onBlur={() => setTimeout(() => setStatusDropdownOpen(false), 150)}
                    aria-haspopup="listbox"
                    aria-expanded={statusDropdownOpen}
                    className="w-full px-2 sm:px-3 py-2 text-sm text-left border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 flex items-center justify-between"
                  >
                    <span className="truncate">
                      {selectedStatuses.length === 0
                        ? t('all')
                        : selectedStatuses.map(s => t(s.toLowerCase())).join(', ')}
                    </span>
                    <span className="ml-2 text-gray-400 text-xs">▾</span>
                  </button>
                  {statusDropdownOpen && (
                    <ul
                      role="listbox"
                      aria-multiselectable="true"
                      className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg py-1"
                    >
                      <li
                        role="option"
                        aria-selected={selectedStatuses.length === 0}
                        onMouseDown={(e) => { e.preventDefault(); setFilters(prev => ({ ...prev, status: '' })); }}
                        className="px-3 py-1.5 text-sm cursor-pointer text-gray-700 hover:bg-gray-50"
                      >
                        {t('all')}
                      </li>
                      {STATUS_OPTIONS.map(opt => (
                        <li
                          key={opt}
                          role="option"
                          aria-selected={selectedStatuses.includes(opt)}
                          onMouseDown={(e) => { e.preventDefault(); toggleStatus(opt); }}
                          className="px-3 py-1.5 text-sm cursor-pointer text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <input type="checkbox" readOnly checked={selectedStatuses.includes(opt)} className="pointer-events-none" />
                          {t(opt.toLowerCase())}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

            </div>
            {!loading && scanResults.length > 0 && (
              <div className="mt-2 border-t border-gray-200 pt-2 lg:hidden">
                <div className="grid grid-cols-[minmax(0,1fr)_4rem_repeat(3,2.5rem)] items-center gap-2">
                  <label className="flex min-h-11 items-center gap-3 px-2 text-sm font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={areAllRowsSelected}
                      ref={(element) => {
                        if (element) {
                          element.indeterminate = scanResults.some(scan => selectedRows.has(scan.id)) && !areAllRowsSelected;
                        }
                      }}
                      onChange={handleSelectAll}
                      className="h-5 w-5 cursor-pointer"
                      aria-label="Select all"
                    />
                    Select all
                  </label>
                  {selectedRows.size > 0 ? (
                    <button
                      onClick={handleBulkDelete}
                      disabled={deleteBusy}
                      className="inline-flex h-9 w-full items-center justify-center gap-1 rounded-lg bg-red-600 px-1 text-white hover:bg-red-700 disabled:bg-gray-400"
                      title="Delete selected rows"
                      aria-label="Delete selected rows"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a1.995 1.995 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 011-1h4a1 1 0 011 1v3M4 7h16" />
                      </svg>
                      <span className="text-xs">{selectedRows.size}</span>
                    </button>
                  ) : (
                    <span aria-hidden="true" />
                  )}
                  <button
                    onClick={handleArchiveExpired}
                    disabled={loading || scanResults.length === 0}
                    className="inline-flex h-9 w-10 items-center justify-center rounded-lg bg-orange-600 text-white hover:bg-orange-700 disabled:bg-gray-400"
                    title="Clean up expired and invalid positions"
                    aria-label="Clean up expired and invalid positions"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                  </button>
                  <button
                    onClick={handleVerifyDisplayedInParallel}
                    disabled={loading || scanResults.length === 0 || verifyingScans.size > 0}
                    className="inline-flex h-9 w-10 items-center justify-center rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400"
                    title="Verify Status"
                    aria-label="Verify Status"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </button>
                  <button
                    onClick={fetchScanResults}
                    disabled={loading}
                    className="inline-flex h-9 w-10 items-center justify-center rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400"
                    title="Refresh table data"
                    aria-label="Refresh table data"
                  >
                    <svg className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 11a8.1 8.1 0 00-15.5-2M4 5v4h4M4 13a8.1 8.1 0 0015.5 2M20 19v-4h-4" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-100 text-red-800 p-3 sm:p-4 rounded-lg mb-4 sm:mb-6 text-sm">
              {error}
            </div>
          )}

        </div>


          {/* Results actions */}
        <div className="flex-1 lg:min-h-0 flex flex-col">
          <div className="mb-0 lg:mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 flex-none">
            {/* Action Buttons Group */}
            <div className="hidden w-full flex-wrap items-center justify-start gap-2 lg:flex lg:w-full">
              <button
                onClick={handleArchiveExpired}
                disabled={loading || scanResults.length === 0}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700 disabled:bg-gray-400 transition-all duration-200 shadow-sm hover:shadow-md"
                title="Clean up expired and invalid positions"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </button>

              <button
                onClick={handleVerifyDisplayedInParallel}
                disabled={loading || scanResults.length === 0 || verifyingScans.size > 0}
                className="flex items-center justify-center p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
                title="Verify Status"
                aria-label="Verify Status"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
              
              {selectedRows.size > 0 && (
                <button
                  onClick={handleBulkDelete}
                  disabled={deleteBusy}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:bg-gray-400 transition-all duration-200 shadow-sm hover:shadow-md"
                  title="Delete selected rows"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span className="text-xs">{selectedRows.size}</span>
                </button>
              )}
              
              <button
                onClick={fetchScanResults}
                disabled={loading}
                className="ml-auto flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-all duration-200 shadow-sm hover:shadow-md"
                title="Refresh table data"
              >
                <svg 
                  className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M20 11a8.1 8.1 0 00-15.5-2M4 5v4h4M4 13a8.1 8.1 0 0015.5 2M20 19v-4h-4"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Table Container - Mobile Optimized */}
          <div className="flex-1 lg:min-h-0 flex flex-col">
          <div className="bg-white rounded-lg shadow flex-1 lg:min-h-0 flex flex-col lg:overflow-hidden">
            <div ref={mobileResultsRef} className="lg:hidden px-3 pb-3 pt-0 space-y-3">
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                </div>
              ) : scanResults.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-500">
                  {t('no_scan_results')}
                </div>
              ) : (
                <>
                {getSortedResults(scanResults).map((scan) => {
                  const symbolKey = String(scan?.symbol || '').trim().toUpperCase();
                  const normalizedSymbolKey = normalizeQuoteSymbolKey(symbolKey);
                  const livePrice = getLiveQuoteForSymbol(livePrices, scan.symbol)
                    || livePrices[normalizedSymbolKey]
                    || livePrices[symbolKey];
                  const livePriceValue = getLivePriceValue(livePrice);
                  const changePercent = getLivePriceChangePercent(livePrice);
                  const livePriceTimestamp = formatLivePriceTimestamp(livePrice?.source_timestamp);
                  const scanDate = formatCompactDateNY(scan.scan_timestamp);
                  const entryFlash = livePriceValue !== null && scan.entry_price && scan.target_price
                    ? scan.signal_type === 'CALL'
                      ? (livePriceValue > scan.entry_price && livePriceValue < scan.target_price ? getFlashColor(scan) : null)
                      : (livePriceValue < scan.entry_price && livePriceValue > scan.target_price ? getFlashColor(scan) : null)
                    : null;
                  const stopPriceHit = livePriceValue !== null && scan.stop_price && (
                    scan.signal_type === 'CALL'
                      ? livePriceValue <= scan.stop_price
                      : livePriceValue >= scan.stop_price
                  );
                  const targetPriceHit = livePriceValue !== null && scan.target_price && (
                    scan.signal_type === 'CALL'
                      ? livePriceValue >= scan.target_price
                      : livePriceValue <= scan.target_price
                  );

                  return (
                    <article
                      key={scan.id}
                      className={`rounded-lg border p-3 shadow-sm ${
                        openActionsMenu === scan.id
                          ? 'border-blue-300 bg-blue-50 ring-1 ring-blue-200'
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-1">
                        <div className="flex min-w-0 flex-1 items-center justify-between gap-1">
                          <input
                            type="checkbox"
                            checked={selectedRows.has(scan.id)}
                            onChange={() => handleSelectRow(scan.id)}
                            className="h-4 w-4 shrink-0 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            aria-label={`Select ${scan.symbol}`}
                          />
                          <CopySymbol symbol={scan.symbol} />
                          {scan.has_signal && scan.signal_type ? (
                            <>
                              <span
                                className={`shrink-0 text-xs font-semibold ${
                                  scan.signal_type === 'CALL'
                                    ? 'text-green-600'
                                    : 'text-red-600'
                                }`}
                              >
                                {scan.signal_type === 'CALL' ? 'Call' : 'Put'}
                              </span>
                              <button
                                type="button"
                                onClick={(event) => openSignalChartModal(scan, event)}
                                title={`Open signal chart (${scan.interval || '5m'} candles)`}
                                aria-label={`Open ${scan.symbol} ${scan.signal_type} chart`}
                                className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded ${
                                  scan.signal_type === 'CALL'
                                    ? 'text-green-600 hover:bg-green-100 hover:text-green-800'
                                    : 'text-red-600 hover:bg-red-100 hover:text-red-800'
                                }`}
                              >
                                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" aria-hidden="true">
                                  {scan.signal_type === 'CALL' ? (
                                    <>
                                      <path d="M3 16.5h14M4.5 13l3.2-3.2 2.5 2.3 5.3-6" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                                      <path d="M12.8 6.1H15.5v2.7" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                                    </>
                                  ) : (
                                    <>
                                      <path d="M3 16.5h14M4.5 7l3.2 3.2 2.5-2.3 5.3 6" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                                      <path d="M12.8 13.9H15.5v-2.7" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                                    </>
                                  )}
                                </svg>
                              </button>
                            </>
                          ) : (
                            <span className="shrink-0 text-xs text-gray-400">No signal</span>
                          )}
                          <div className="shrink-0">
                            <div className="flex items-baseline gap-1">
                              <p className="text-xs text-gray-500">{t('scan_date')}</p>
                              <p className="whitespace-nowrap text-[10px] tabular-nums text-gray-500">
                                <ScannedAge timestamp={scan.scan_timestamp} />
                              </p>
                            </div>
                            <p className="whitespace-nowrap text-[10px] text-gray-900">{scanDate}</p>
                          </div>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              setOpenActionsMenu(openActionsMenu === scan.id ? null : scan.id);
                            }}
                            aria-label={`${openActionsMenu === scan.id ? 'Hide' : 'Show'} actions for ${scan.symbol}`}
                            aria-expanded={openActionsMenu === scan.id}
                            className={`shrink-0 rounded px-2 py-1 text-lg leading-none text-gray-600 hover:bg-blue-100 hover:text-gray-900 ${
                              openActionsMenu === scan.id ? 'bg-blue-100 text-gray-900' : ''
                            }`}
                          >
                            ⋮
                          </button>
                        </div>
                      </div>

                      <div className="mt-3 grid grid-cols-4 gap-2 border-t border-gray-100 pt-3">
                        <div>
                          <p className="text-xs text-gray-500">Live price</p>
                          {livePriceValue !== null ? (
                            <div>
                              <p className="text-gray-900">${livePriceValue.toFixed(2)}</p>
                              {changePercent !== null && changePercent !== undefined && (
                                <p className={`text-xs ${changePercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {changePercent >= 0 ? '▲' : '▼'} {changePercent > 0 ? '+' : ''}{changePercent.toFixed(2)}%
                                </p>
                              )}
                              <p
                                className="text-[10px] text-gray-500"
                                title={livePriceTimestamp
                                  ? `${livePriceTimestamp.clock} ET, ${livePriceTimestamp.date}. ${t('quote_feed_disclaimer')}`
                                  : undefined}
                              >
                                {livePriceTimestamp
                                  ? <LivePriceAge timestamp={livePriceTimestamp} />
                                  : t('quote_timestamp_unknown')}
                              </p>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-400">{wsConnected ? '⏳ Loading live...' : '⚠️ Disconnected'}</p>
                          )}
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Volume</p>
                          <VolumeSparkline scan={scan} tableRootRef={mobileResultsRef} liveQuote={livePrice} />
                        </div>
                        <div className="col-start-1 row-start-2">
                          <p className="text-xs text-gray-500">Entry</p>
                          <p className="flex items-center px-1 text-sm text-gray-900">
                            {formatCurrency(scan.entry_price)}
                            {entryFlash && <span className={`flash-dot ${entryFlash}`} aria-hidden="true" />}
                          </p>
                        </div>
                        <div className="col-start-2 row-start-2">
                          <p className="text-xs text-gray-500">Stop</p>
                          <p className="flex items-center px-1 text-sm text-red-600">
                            {formatCurrency(scan.stop_price)}
                            {stopPriceHit && <span className={`flash-dot ${getFlashColor(scan)}`} aria-hidden="true" />}
                          </p>
                        </div>
                        <div className="col-start-3 row-start-2">
                          <p className="text-xs text-gray-500">Target</p>
                          <p className={`flex items-center px-1 text-sm ${scan.signal_type === 'CALL' ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(scan.target_price)}
                            {targetPriceHit && <span className={`flash-dot ${getFlashColor(scan)}`} aria-hidden="true" />}
                          </p>
                        </div>
                        <div className="col-start-3 row-start-1">
                          <p className="text-xs text-gray-500">Period</p>
                          <p className="whitespace-nowrap text-[10px] text-gray-900">
                            {scan.period || '-'}
                          </p>
                        </div>
                        <div className="col-start-4 row-start-1">
                          <p className="text-xs text-gray-500">Interval</p>
                          <p className="whitespace-nowrap text-[10px] text-gray-900">
                            {scan.interval || '-'}
                          </p>
                        </div>
                        <div className="col-start-4 row-start-2">
                          <p className="text-xs text-gray-500">Status</p>
                          {getStatusBadge(scan.status, scan.id)}
                          <StatusAge timestamp={scan.status_datetime} />
                        </div>
                      </div>

                      {openActionsMenu === scan.id && (
                        <div className="relative mt-3 flex flex-wrap items-end justify-center gap-x-6 gap-y-3 rounded-md border-t-2 border-blue-200 bg-blue-50 px-3 py-3">
                          <button
                            type="button"
                            onClick={() => setOpenActionsMenu(null)}
                            aria-label={`Close actions for ${scan.symbol}`}
                            className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded text-lg leading-none text-gray-500 hover:bg-blue-100 hover:text-gray-900"
                          >
                            ×
                          </button>
                          <div className="flex flex-col items-center gap-1 text-sm font-medium text-gray-700">
                            <span className="h-5 leading-5">Gain / Risk-reward</span>
                            <span className="flex h-8 items-center gap-1 font-normal">
                              <span className={scan.gain_percent >= 0 ? 'font-semibold text-green-600' : 'font-semibold text-red-600'}>
                                {scan.gain_percent != null ? `${scan.gain_percent >= 0 ? '+' : ''}${scan.gain_percent.toFixed(2)}%` : '-'}
                              </span>
                              <span className="text-gray-400">/</span>
                              <span className="text-gray-900">{scan.risk_reward ? scan.risk_reward.toFixed(2) : '-'}</span>
                            </span>
                          </div>
                          <label className="flex flex-col items-center gap-1 text-sm font-medium text-gray-700">
                            <span className="h-5 leading-5">Executed?</span>
                            <span className="flex h-8 items-center justify-center">
                              <input
                                type="checkbox"
                                checked={editingRows[scan.id]?.executed !== undefined ? editingRows[scan.id].executed : (scan.executed || false)}
                                onChange={(event) => handleExecutedChange(scan.id, event.target.checked)}
                                className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                aria-label={`Executed for ${scan.symbol}`}
                              />
                            </span>
                          </label>
                          <div className="flex items-end gap-2">
                            <label className="flex flex-col gap-1 text-sm font-medium text-gray-700" htmlFor={`mobile-profit-${scan.id}`}>
                              <span className="h-5 leading-5">Profit ($)</span>
                              <input
                                id={`mobile-profit-${scan.id}`}
                                type="number"
                                step="0.01"
                                value={editingRows[scan.id]?.profit !== undefined ? editingRows[scan.id].profit : (scan.profit || '')}
                                onChange={(event) => handleProfitChange(scan.id, event.target.value)}
                                placeholder="0.00"
                                className="h-8 w-32 rounded border border-gray-300 px-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </label>
                            <button
                              type="button"
                              onClick={() => handleSaveExecution(scan.id, scan)}
                              disabled={savingRows.has(scan.id)}
                              className={`h-8 rounded px-3 text-xs font-medium text-white ${
                                savingRows.has(scan.id) ? 'cursor-not-allowed bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
                              }`}
                            >
                              {savingRows.has(scan.id) ? 'Saving...' : 'Save'}
                            </button>
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
                </>
              )}
            </div>
            
            <div
              ref={tableScrollRef}
              className="table-scrollbars hidden lg:flex flex-1 min-h-0 overflow-scroll w-full"
              style={{ WebkitOverflowScrolling: 'touch', scrollbarGutter: 'stable both-edges' }}
            >
              <table className="w-full min-w-max divide-y divide-gray-200 [&_th]:px-1.5 [&_td:not([colspan])]:px-1.5">
                <thead className="bg-gray-50 text-xs sticky top-0 z-20">
                  <tr>
                    <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        title="Select/Deselect all rows">
                      <input
                        type="checkbox"
                        checked={areAllRowsSelected}
                        onChange={handleSelectAll}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                      />
                    </th>
                    <th 
                      onClick={() => handleSort('symbol')}
                      className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      title="Stock ticker symbol (e.g., AAPL, TSLA)"
                    >
                      {t('symbol')} <SortIcon columnKey="symbol" />
                    </th>
                    <th 
                      onClick={() => handleSort('signal_type')}
                      className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      title="CALL = Bullish signal (expect price to rise) | PUT = Bearish signal (expect price to fall)"
                    >
                      {t('put_call')} <SortIcon columnKey="signal_type" />
                    </th>
                    <th 
                      onClick={() => handleSort('live_price')}
                      className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      title={t('quote_feed_disclaimer')}
                    >
                      {t('live_price')} <SortIcon columnKey="live_price" />
                    </th>
                    <th
                      className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      title="Last 5 volume bars for the selected period and interval"
                    >
                      Volume
                    </th>
                    <th
                      className="px-2 sm:px-3 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                      title="Open the signal chart"
                    >
                      Chart
                    </th>
                    <th 
                      onClick={() => handleSort('entry_price')}
                      className="px-2 sm:px-3 py-2 sm:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      title="Recommended entry price to open position"
                    >
                      {t('entry')} <SortIcon columnKey="entry_price" />
                    </th>
                    <th
                      onClick={() => handleSort('flash_entry')}
                      className="w-4 pl-1.5 pr-2 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 cursor-pointer hover:bg-gray-100"
                      title="Sort by flashing"
                    >
                      <SortIcon columnKey="flash_entry" />
                    </th>
                    <th 
                      onClick={() => handleSort('stop_price')}
                      className="px-2 sm:px-3 py-2 sm:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      title="Stop-loss price to limit losses if trade goes wrong"
                    >
                      {t('stop')} <SortIcon columnKey="stop_price" />
                    </th>
                    <th
                      onClick={() => handleSort('flash_stop')}
                      className="w-4 pl-1.5 pr-2 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 cursor-pointer hover:bg-gray-100"
                      title="Sort by flashing"
                    >
                      <SortIcon columnKey="flash_stop" />
                    </th>
                    <th 
                      onClick={() => handleSort('target_price')}
                      className="px-2 sm:px-3 py-2 sm:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      title="Target price to take profit"
                    >
                      {t('target')} <SortIcon columnKey="target_price" />
                    </th>
                    <th
                      onClick={() => handleSort('flash_target')}
                      className="w-4 pl-1.5 pr-2 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 cursor-pointer hover:bg-gray-100"
                      title="Sort by flashing"
                    >
                      <SortIcon columnKey="flash_target" />
                    </th>
                    <th 
                      onClick={() => handleSort('gain_percent')}
                      className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      title="Formula: (Target - Entry) / Entry × 100 | Potential gain % if target is reached"
                    >
                      Gain % <SortIcon columnKey="gain_percent" />
                    </th>
                    <th 
                      onClick={() => handleSort('risk_reward')}
                      className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      title="Formula: (Target - Entry) / (Entry - Stop) | Risk/Reward ratio. Higher is better (e.g., 2.5 = $2.50 gain per $1 risk)"
                    >
                      {t('risk_reward')} <SortIcon columnKey="risk_reward" />
                    </th>
                    <th 
                      onClick={() => handleSort('status')}
                      className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      title="Current signal status: PENDING = Wait | ENTER_NOW = Enter position | TARGET_HIT = Profit taken | STOPPED = Loss taken | EXPIRED = Signal expired"
                    >
                      {t('status')} <SortIcon columnKey="status" />
                    </th>
                    <th
                      onClick={() => handleSort('period')}
                      className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      title="Scan period"
                    >
                      Period <SortIcon columnKey="period" />
                    </th>
                    <th
                      onClick={() => handleSort('interval')}
                      className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      title="Scan candle interval"
                    >
                      Interval <SortIcon columnKey="interval" />
                    </th>
                    <th 
                      onClick={() => handleSort('scan_timestamp')}
                      className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      title="Date and time when this scan result was generated (NY timezone)"
                    >
                      SCANNED TIME <SortIcon columnKey="scan_timestamp" />
                    </th>
                    <th
                      className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      title="Elapsed time since the scan in days:hours:minutes:seconds"
                    >
                      SCANNED AGE
                    </th>
                    <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        title="Row options: Executed, Profit, Verify, and Delete">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan="20" className="px-2 sm:px-3 py-8 sm:py-12 text-center">
                        <div className="flex justify-center">
                          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-blue-600"></div>
                        </div>
                      </td>
                    </tr>
                  ) : scanResults.length === 0 ? (
                    <tr>
                      <td colSpan="20" className="px-2 sm:px-3 py-8 sm:py-12 text-center text-gray-500 text-sm">
                        {t('no_scan_results')}
                      </td>
                    </tr>
                  ) : (
                    getSortedResults(scanResults).map((scan) => {
                      const symbolKey = String(scan?.symbol || '').trim().toUpperCase();
                      const normalizedSymbolKey = normalizeQuoteSymbolKey(symbolKey);
                      const livePrice = getLiveQuoteForSymbol(livePrices, scan.symbol)
                        || livePrices[normalizedSymbolKey]
                        || livePrices[symbolKey];
                      const displayLivePrice = livePrice;
                      const livePriceValue = getLivePriceValue(livePrice);
                      const displayLivePriceValue = livePriceValue;
                      const fallbackPrice = scan?.last_close;
                      const changePercent = getLivePriceChangePercent(displayLivePrice);
                      const livePriceTimestamp = formatLivePriceTimestamp(displayLivePrice?.source_timestamp);
                      const scanDate = formatDateNY(scan.scan_timestamp);
                      
                      return (
                        <React.Fragment key={scan.id}>
                          <tr className={openActionsMenu === scan.id ? 'border-b-0 bg-blue-50' : 'hover:bg-gray-50'}>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedRows.has(scan.id)}
                              onChange={() => handleSelectRow(scan.id)}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                            />
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap">
                            <span className="text-xs sm:text-sm text-gray-900">
                              <CopySymbol symbol={scan.symbol} />
                            </span>
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap">
                            {scan.has_signal && scan.signal_type ? (
                              <span className={`text-xs font-semibold ${
                                scan.signal_type === 'CALL'
                                  ? 'text-green-600'
                                  : 'text-red-600'
                              }`}>
                                {scan.signal_type === 'CALL' ? 'Call' : 'Put'}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap">
                            {displayLivePriceValue !== null ? (
                              <div className="flex flex-col">
                                <span className="                                text-xs sm:text-sm text-gray-900">
                                  ${displayLivePriceValue.toFixed(2)}
                                </span>
                                <span
                                  className="text-[10px] text-gray-500"
                                  title={livePriceTimestamp
                                    ? `${livePriceTimestamp.clock} ET, ${livePriceTimestamp.date}. ${t('quote_feed_disclaimer')}`
                                    : t('quote_feed_disclaimer')}
                                >
                                  {livePriceTimestamp ? (
                                    <>
                                      <span
                                        key={`${normalizedSymbolKey}-${displayLivePrice?._timeRevision || 0}`}
                                        className="quote-time-flash"
                                      >
                                        <LivePriceAge timestamp={livePriceTimestamp} />
                                      </span>
                                    </>
                                  ) : t('quote_timestamp_unknown')}
                                </span>
                                {changePercent !== null && changePercent !== undefined && (
                                  <span className={`text-xs ${changePercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {changePercent >= 0 ? '▲' : '▼'} {changePercent > 0 ? '+' : ''}{changePercent.toFixed(2)}%
                                  </span>
                                )}
                              </div>
                            ) : (
                              wsConnected ? (
                                <div className="flex flex-col">
                                  <span className="text-xs text-gray-400">⏳ Loading live...</span>
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400">⚠️ Disconnected</span>
                              )
                            )}
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap align-middle">
                            <VolumeSparkline scan={scan} tableRootRef={tableScrollRef} liveQuote={displayLivePrice} />
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap text-center">
                            {scan.has_signal && scan.signal_type ? (
                              <button
                                type="button"
                                onClick={(event) => openSignalChartModal(scan, event)}
                                title={`Open signal chart (${scan.interval || '5m'} candles)`}
                                aria-label={`Open ${scan.symbol} ${scan.signal_type} chart`}
                                className={`inline-flex h-8 w-8 items-center justify-center rounded ${
                                  scan.signal_type === 'CALL'
                                    ? 'text-green-600 hover:bg-green-50 hover:text-green-800'
                                    : 'text-red-600 hover:bg-red-50 hover:text-red-800'
                                }`}
                              >
                                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" aria-hidden="true">
                                  {scan.signal_type === 'CALL' ? (
                                    <>
                                      <path d="M3 16.5h14M4.5 13l3.2-3.2 2.5 2.3 5.3-6" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                                      <path d="M12.8 6.1H15.5v2.7" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                                    </>
                                  ) : (
                                    <>
                                      <path d="M3 16.5h14M4.5 7l3.2 3.2 2.5-2.3 5.3 6" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                                      <path d="M12.8 13.9H15.5v-2.7" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                                    </>
                                  )}
                                </svg>
                              </button>
                            ) : (
                            <span className="text-gray-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="pl-2 pr-0 sm:pl-3 py-3 sm:py-4 whitespace-nowrap text-right text-xs sm:text-sm text-gray-900">
                            {scan.entry_price ? `$${scan.entry_price.toFixed(2)}` : '-'}
                          </td>
                          <td className="w-4 pl-1.5 pr-2 py-3 sm:py-4 text-left align-middle">
                            {livePrice && scan.entry_price && scan.target_price && (
                              scan.signal_type === 'CALL'
                                ? (livePrice.price > scan.entry_price && livePrice.price < scan.target_price)
                                : (livePrice.price < scan.entry_price && livePrice.price > scan.target_price)
                            ) && (
                              <span
                                className={`flash-dot !ml-0 ${getFlashColor(scan)}`}
                                aria-hidden="true"
                              />
                            )}
                          </td>
                          <td className="pl-2 pr-0 sm:pl-3 py-3 sm:py-4 whitespace-nowrap text-right text-xs sm:text-sm text-red-600">
                            {scan.stop_price ? `$${scan.stop_price.toFixed(2)}` : '-'}
                          </td>
                          <td className="w-4 pl-1.5 pr-2 py-3 sm:py-4 text-left align-middle">
                            {livePrice && scan.stop_price && (
                              scan.signal_type === 'CALL'
                                ? livePrice.price <= scan.stop_price
                                : livePrice.price >= scan.stop_price
                            ) && (
                              <span className={`flash-dot !ml-0 ${getFlashColor(scan)}`} aria-hidden="true" />
                            )}
                          </td>
                          <td className={`pl-2 pr-0 sm:pl-3 py-3 sm:py-4 whitespace-nowrap text-right text-xs sm:text-sm ${scan.signal_type === 'CALL' ? 'text-green-600' : 'text-red-600'}`}>
                            {scan.target_price ? `$${scan.target_price.toFixed(2)}` : '-'}
                          </td>
                          <td className="w-4 pl-1.5 pr-2 py-3 sm:py-4 text-left align-middle">
                            {livePrice && scan.target_price && (
                              scan.signal_type === 'CALL'
                                ? livePrice.price >= scan.target_price
                                : livePrice.price <= scan.target_price
                            ) && (
                              <span className={`flash-dot !ml-0 ${getFlashColor(scan)}`} aria-hidden="true" />
                            )}
                          </td>
                          {/* Gain % Column */}
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-semibold">
                            {scan.gain_percent != null ? (
                              <span className={scan.gain_percent >= 0 ? 'text-green-600' : 'text-red-600'}>
                                {scan.gain_percent >= 0 ? '+' : ''}{scan.gain_percent.toFixed(2)}%
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                            {scan.risk_reward ? scan.risk_reward.toFixed(2) : '-'}
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap">
                            {getStatusBadge(scan.status, scan.id)}
                            <StatusAge timestamp={scan.status_datetime} />
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                            {scan.period || '-'}
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                            {scan.interval || '-'}
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500">
                            <span className="block text-xs sm:text-sm text-gray-900">{scanDate.date}</span>
                            {scanDate.time && (
                              <span className="block text-[10px] sm:text-xs text-gray-500">{scanDate.time}</span>
                            )}
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap text-[10px] sm:text-xs tabular-nums text-gray-500">
                            <ScannedAge timestamp={scan.scan_timestamp} />
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm">
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                setOpenActionsMenu(openActionsMenu === scan.id ? null : scan.id);
                              }}
                              aria-label={`${openActionsMenu === scan.id ? 'Hide' : 'Show'} actions for ${scan.symbol}`}
                              aria-expanded={openActionsMenu === scan.id}
                              className={`rounded px-2 py-1 text-lg leading-none text-gray-600 hover:bg-gray-100 hover:text-gray-900 ${
                                openActionsMenu === scan.id ? 'bg-gray-100 text-gray-900' : ''
                              }`}
                            >
                              ⋮
                            </button>
                          </td>
                        </tr>
                        {openActionsMenu === scan.id && (
                          <tr className="border-t-0 bg-blue-50">
                            <td colSpan="20" className="border-b border-l-4 border-blue-200 border-l-blue-500 px-4 py-3">
                              <div className="relative flex flex-wrap items-center justify-center gap-x-6 gap-y-3 pr-8">
                                <button
                                  type="button"
                                  onClick={() => setOpenActionsMenu(null)}
                                  aria-label={`Close actions for ${scan.symbol}`}
                                  className="absolute right-0 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded text-lg leading-none text-gray-500 hover:bg-blue-100 hover:text-gray-900"
                                >
                                  ×
                                </button>
                                <label className="flex flex-col items-center gap-1 text-sm font-medium text-gray-700">
                                  <span className="h-5 leading-5">Executed?</span>
                                  <span className="flex h-8 items-center justify-center">
                                    <input
                                      type="checkbox"
                                      checked={editingRows[scan.id]?.executed !== undefined ? editingRows[scan.id].executed : (scan.executed || false)}
                                      onChange={(event) => handleExecutedChange(scan.id, event.target.checked)}
                                      onClick={(event) => event.stopPropagation()}
                                      className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                      aria-label={`Executed for ${scan.symbol}`}
                                    />
                                  </span>
                                </label>
                                <div className="flex items-end gap-2">
                                  <label className="flex flex-col gap-1 text-sm font-medium text-gray-700" htmlFor={`profit-${scan.id}`}>
                                    <span className="h-5 leading-5">Profit ($)</span>
                                    <input
                                      id={`profit-${scan.id}`}
                                      type="number"
                                      step="0.01"
                                      value={editingRows[scan.id]?.profit !== undefined ? editingRows[scan.id].profit : (scan.profit || '')}
                                      onChange={(event) => handleProfitChange(scan.id, event.target.value)}
                                      onClick={(event) => event.stopPropagation()}
                                      placeholder="0.00"
                                      className="h-8 w-32 rounded border border-gray-300 px-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                  </label>
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleSaveExecution(scan.id, scan);
                                    }}
                                    disabled={savingRows.has(scan.id)}
                                    className={`h-8 rounded px-3 text-xs font-medium text-white transition ${
                                      savingRows.has(scan.id)
                                        ? 'cursor-not-allowed bg-gray-400'
                                        : 'bg-blue-600 hover:bg-blue-700'
                                    }`}
                                    title="Save executed status and profit"
                                  >
                                    {savingRows.has(scan.id) ? 'Saving...' : 'Save'}
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalCount > 0 && (
              <div className="mt-4 grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2 px-2 py-3 bg-white border-t border-gray-200 flex-none sm:flex sm:flex-row sm:justify-between sm:gap-4 sm:px-4">
                <div className="col-span-2 flex items-end justify-center gap-2 sm:col-span-1 sm:justify-start">
                  <select
                    aria-label="Rows per page"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(parseInt(e.target.value));
                      setPage(1); // Reset to first page
                    }}
                    className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="200">200</option>
                    <option value="500">500</option>
                    <option value="1000">1000</option>
                  </select>
                </div>

                <div className="col-span-2 flex items-center justify-center gap-1 sm:col-span-1 sm:gap-2">
                  <button
                    onClick={() => setPage(1)}
                    disabled={page === 1 || loading}
                    className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed sm:px-3"
                    title="First page"
                    aria-label="First page"
                  >
                    ⏮️
                  </button>
                  <button
                    onClick={() => setPage(prev => Math.max(1, prev - 1))}
                    disabled={page === 1 || loading}
                    className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed sm:px-3"
                    title="Previous page"
                    aria-label="Previous page"
                  >
                    ◀️ <span className="hidden sm:inline">Prev</span>
                  </button>
                  
                  <span className="whitespace-nowrap px-1 py-1 text-sm text-gray-700 sm:px-4">
                    Page <span className="font-semibold">{page}</span> of <span className="font-semibold">{Math.ceil(totalCount / pageSize)}</span>
                  </span>
                  
                  <button
                    onClick={() => setPage(prev => prev + 1)}
                    disabled={page >= Math.ceil(totalCount / pageSize) || loading}
                    className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed sm:px-3"
                    title="Next page"
                    aria-label="Next page"
                  >
                    <span className="hidden sm:inline">Next </span>▶️
                  </button>
                  <button
                    onClick={() => setPage(Math.ceil(totalCount / pageSize))}
                    disabled={page >= Math.ceil(totalCount / pageSize) || loading}
                    className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed sm:px-3"
                    title="Last page"
                    aria-label="Last page"
                  >
                    ⏭️
                  </button>
                </div>

                <div className="col-span-2 whitespace-nowrap text-center text-xs text-gray-600 sm:col-span-1 sm:text-right sm:text-sm">
                  Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} of {totalCount}
                </div>
              </div>
            )}
          </div>
          </div>

        </div>
        </div>
      </div>

      {deleteDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={closeDeleteDialog}
          onKeyDown={(e) => { if (e.key === 'Escape') closeDeleteDialog(); }}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-dialog-title"
            className="relative w-full max-w-md rounded-xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeDeleteDialog}
              disabled={deleteBusy}
              aria-label="Close"
              className="absolute right-3 top-3 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="flex items-start gap-3 p-5 pr-10">
              <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-red-100 text-red-600">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <h3 id="delete-dialog-title" className="text-base font-semibold text-gray-900">
                  {deleteDialogTitle}
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  {deleteDialog.loadingItems
                    ? 'Loading expired and invalid predictions...'
                    : deleteDialog.type === 'expired' && deleteDialog.ids.length === 0
                      ? 'No expired or invalid predictions to clean up.'
                      : deleteDialog.type === 'expired'
                        ? `${deleteDialog.ids.length} expired/invalid prediction(s) across all pages will be removed. Click × to keep any of them.`
                        : deleteDialog.ids.length === 1
                          ? 'This prediction will be removed from your list.'
                          : `${deleteDialog.ids.length} predictions will be removed from your list.`}{' '}
                  {deleteDialog.ids.length > 0 && 'This cannot be undone.'}
                </p>
                {deleteDialog.type === 'expired' && deleteDialogScans.length > 0 && (
                  <ul className="mt-3 max-h-64 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50">
                    {deleteDialogScans.map(scan => (
                      <li key={scan.id} className="flex items-center gap-3 px-3 py-1.5 text-sm">
                        <span className="w-16 flex-none font-semibold text-gray-800">{scan.symbol}</span>
                        <span className={`w-10 flex-none text-xs font-medium ${scan.signal_type === 'CALL' ? 'text-green-600' : scan.signal_type === 'PUT' ? 'text-red-600' : 'text-gray-400'}`}>
                          {scan.signal_type || '-'}
                        </span>
                        <span className={`flex-none rounded px-1.5 py-0.5 text-xs ${scan.status === 'INVALID' ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-700'}`}>
                          {scan.status}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-right text-xs text-gray-500">
                          {scan.scan_timestamp ? new Date(scan.scan_timestamp).toLocaleDateString() : ''}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFromDeleteDialog(scan.id)}
                          disabled={deleteBusy}
                          aria-label={`Keep ${scan.symbol}`}
                          title="Keep this prediction"
                          className="flex-none text-lg leading-none text-gray-400 hover:text-red-600 disabled:opacity-50"
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {deleteDialog.type !== 'expired' && deleteDialogScans.length > 0 && (
                  <div className="mt-3 max-h-40 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-2">
                    <div className="grid grid-cols-2 gap-1.5">
                      {deleteDialogScans.slice(0, 40).map(scan => (
                        <span
                          key={scan.id}
                          className="flex items-center gap-1 rounded-md bg-white px-2 py-0.5 text-xs font-medium text-gray-700 border border-gray-200"
                        >
                          <span className="font-semibold">{scan.symbol}</span>
                          {scan.signal_type && (
                            <span className={scan.signal_type === 'CALL' ? 'text-green-600' : 'text-red-600'}>
                              {scan.signal_type}
                            </span>
                          )}
                          {scan.status && <span className="truncate text-gray-400">{scan.status}</span>}
                          <button
                            type="button"
                            onClick={() => removeFromDeleteDialog(scan.id)}
                            disabled={deleteBusy}
                            aria-label={`Keep ${scan.symbol}`}
                            title="Keep this prediction"
                            className="ml-auto leading-none text-gray-400 hover:text-red-600 disabled:opacity-50"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      {deleteDialogScans.length > 40 && (
                        <span className="col-span-2 px-1 text-xs text-gray-500">+{deleteDialogScans.length - 40} more</span>
                      )}
                    </div>
                  </div>
                )}
                {deleteError && (
                  <div className="mt-3 rounded-lg bg-red-50 p-2 text-sm text-red-700">{deleteError}</div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 rounded-b-xl bg-gray-50 px-5 py-3">
              <button
                type="button"
                onClick={closeDeleteDialog}
                disabled={deleteBusy}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                autoFocus
                onClick={confirmDelete}
                disabled={deleteBusy || deleteDialog.loadingItems || deleteDialog.ids.length === 0}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:bg-red-300"
              >
                {deleteBusy
                  ? 'Deleting...'
                  : deleteDialog.ids.length === 1
                    ? 'Delete'
                    : `Delete ${deleteDialog.ids.length}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {verifyDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={closeVerifyDialog}
          onKeyDown={(e) => { if (e.key === 'Escape') closeVerifyDialog(); }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="verify-dialog-title"
            className="relative w-full max-w-lg rounded-xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeVerifyDialog}
              aria-label="Close"
              className="absolute right-3 top-3 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="p-5 pr-10">
              <h3 id="verify-dialog-title" className="text-base font-semibold text-gray-900">Verify Status</h3>
              <p className="mt-1 text-sm text-gray-600">
                {verifyDialog.finished
                  ? `Done: ${Object.values(verifyDialog.results).filter(r => r.state === 'ok').length} verified, ${Object.values(verifyDialog.results).filter(r => r.state === 'error').length} failed.`
                  : verifyDialog.running
                    ? 'Verifying...'
                    : verifyDialog.items.length === 0
                      ? 'No predictions selected.'
                      : `${verifyDialog.items.length} prediction(s) will be checked for current status.`}
              </p>
              {verifyDialog.items.length > 0 && (
                <ul className="mt-3 max-h-72 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50">
                  {verifyDialog.items.map(scan => {
                    const r = verifyDialog.results[scan.id];
                    return (
                      <li key={scan.id} className="flex items-center gap-3 px-3 py-1.5 text-sm">
                        <span className="w-16 flex-none font-semibold text-gray-800">{scan.symbol}</span>
                        <span className={`w-10 flex-none text-xs font-medium ${scan.signal_type === 'CALL' ? 'text-green-600' : scan.signal_type === 'PUT' ? 'text-red-600' : 'text-gray-400'}`}>
                          {scan.signal_type || '-'}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-xs" title={r?.message || ''}>
                          {!r && <span className="text-gray-500">{scan.status}</span>}
                          {r?.state === 'running' && <span className="text-blue-600">{r.from} · verifying...</span>}
                          {r?.state === 'ok' && (
                            <span className={r.from !== r.to ? 'font-semibold text-green-700' : 'text-gray-600'}>
                              {r.from === r.to ? `${r.to} (no change)` : `${r.from} → ${r.to}`}
                            </span>
                          )}
                          {r?.state === 'error' && <span className="text-red-600">Failed: {r.message}</span>}
                        </span>
                        {!verifyDialog.running && !verifyDialog.finished && (
                          <button
                            type="button"
                            onClick={() => removeFromVerifyDialog(scan.id)}
                            aria-label={`Skip ${scan.symbol}`}
                            title="Skip this prediction"
                            className="flex-none text-lg leading-none text-gray-400 hover:text-red-600"
                          >
                            ×
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    role="switch"
                    checked={autoVerify.enabled}
                    onChange={(e) => updateAutoVerify({ enabled: e.target.checked })}
                    disabled={!autoVerifyLoaded || autoVerifySave?.state === 'saving'}
                    className="h-4 w-4 cursor-pointer rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  Auto-verify {autoVerify.enabled ? 'ON' : 'OFF'}
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  Every
                  <select
                    value={autoVerify.interval_seconds}
                    onChange={(e) => updateAutoVerify({ interval_seconds: Number(e.target.value) })}
                    disabled={!autoVerify.enabled || !autoVerifyLoaded || autoVerifySave?.state === 'saving'}
                    className="rounded-lg border border-gray-300 px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    {AUTO_VERIFY_INTERVALS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                    {!AUTO_VERIFY_INTERVALS.some(opt => opt.value === autoVerify.interval_seconds) && (
                      <option value={autoVerify.interval_seconds}>Custom: {autoVerify.interval_seconds} seconds</option>
                    )}
                  </select>
                </label>
                <div className="flex w-full flex-wrap items-end gap-2 text-sm text-gray-700">
                  <span className="w-full">Custom interval</span>
                  {['hours', 'minutes', 'seconds'].map(part => (
                    <label key={part} className="flex flex-col gap-1">
                      <span className="capitalize">{part}</span>
                      <input
                        type="number"
                        min="0"
                        max={part === 'hours' ? 24 : 59}
                        step="1"
                        value={autoVerifyCustomTime[part]}
                        onChange={event => setAutoVerifyCustomTime(previous => ({
                          ...previous, [part]: event.target.value,
                        }))}
                        disabled={!autoVerify.enabled || !autoVerifyLoaded || autoVerifySave?.state === 'saving'}
                        className="w-20 rounded-lg border border-gray-300 px-2 py-1 disabled:bg-gray-100"
                      />
                    </label>
                  ))}
                  <button
                    type="button"
                    onClick={applyCustomAutoVerifyInterval}
                    disabled={!autoVerify.enabled || !autoVerifyLoaded || autoVerifySave?.state === 'saving'}
                    className="rounded-lg bg-blue-600 px-3 py-1 text-white disabled:bg-gray-300"
                  >
                    Save
                  </button>
                  {autoVerifyCustomError && (
                    <p role="alert" className="w-full text-xs text-red-600">{autoVerifyCustomError}</p>
                  )}
                </div>
                {autoVerifySave && (
                  <p
                    role="status"
                    className={`w-full text-xs ${autoVerifySave.state === 'ok' ? 'text-green-600' : autoVerifySave.state === 'error' ? 'text-red-600' : 'text-gray-500'}`}
                  >
                    {autoVerifySave.message}
                  </p>
                )}
                {autoVerify.enabled && (
                  <p
                    role="status"
                    className={`w-full text-xs ${
                      !autoVerifyRunning && autoVerifyLastRun?.tone === 'error' ? 'text-red-600' : 'text-gray-500'
                    }`}
                  >
                    {autoVerifyRunning
                      ? 'Refreshing background results...'
                      : autoVerifyLastRun
                        ? `${autoVerifyLastRun.message} · ${autoVerifyLastRun.time.toLocaleTimeString()}`
                        : 'Runs in the background for your account, even when this page is closed.'}
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 rounded-b-xl bg-gray-50 px-5 py-3">
              <button
                type="button"
                onClick={closeVerifyDialog}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                {verifyDialog.finished ? 'Close' : verifyDialog.running ? 'Close (keeps running)' : 'Cancel'}
              </button>
              {!verifyDialog.finished && (
                <button
                  type="button"
                  autoFocus
                  onClick={confirmVerify}
                  disabled={verifyDialog.running || verifyDialog.items.length === 0}
                  className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:bg-green-300"
                >
                  {verifyDialog.running ? 'Verifying...' : `Verify ${verifyDialog.items.length}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {autoVerifyDetails && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4"
          onClick={() => setAutoVerifyDetails(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="auto-verify-details-title"
            className="relative w-full max-w-lg rounded-xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.key === 'Escape' && setAutoVerifyDetails(null)}
          >
            <button
              type="button"
              onClick={() => setAutoVerifyDetails(null)}
              aria-label="Close"
              className="absolute right-3 top-3 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="p-5 pr-10">
              <h3 id="auto-verify-details-title" className="text-base font-semibold text-gray-900">
                {autoVerifyDetails.type === 'scan' ? 'Scan completed' : 'Auto-verify details'}
              </h3>
              {autoVerifyDetails.type === 'scan' ? (
                <>
                  <div className="mt-1 text-sm text-gray-600">
                    <p>
                      {`${autoVerifyDetails.time.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric' })}, ${autoVerifyDetails.time.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', second: '2-digit' })} ET`}
                    </p>
                    <p className="font-medium text-gray-800">
                      {autoVerifyDetails.scanned} symbol{autoVerifyDetails.scanned === 1 ? '' : 's'} scanned
                      {autoVerifyDetails.signals.length === 0 ? ' · No signals found' : ` · Found ${autoVerifyDetails.signals.length} signal${autoVerifyDetails.signals.length === 1 ? '' : 's'}`}
                    </p>
                    {autoVerifyDetails.existing_predictions > 0 && (
                      <p>{t('scan_predictions_exist', { count: autoVerifyDetails.existing_predictions })}</p>
                    )}
                  </div>
                  <ScanFindingsSummary notification={autoVerifyDetails} />
                  {autoVerifyDetails.signals.length === 0 ? (
                    <p className="mt-3 rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
                      ✅ Scanned {autoVerifyDetails.scanned} symbol{autoVerifyDetails.scanned === 1 ? '' : 's'} - No signals found
                    </p>
                  ) : (
                    <ul className="mt-3 max-h-[50vh] divide-y divide-gray-100 overflow-x-hidden overflow-y-auto overscroll-contain rounded-lg border border-gray-200 bg-gray-50">
                      {autoVerifyDetails.signals.map((signal, index) => (
                        <li key={`${signal.symbol}-${index}`} className="px-3 py-2 text-sm">
                          <ConfirmationCandle prediction={signal} />
                          <div className="flex items-center gap-2">
                            <span className="w-11 flex-none font-semibold text-gray-800">{signal.symbol}</span>
                            <span className={`w-7 flex-none text-[11px] font-medium ${signal.signal_type === 'CALL' ? 'text-green-600' : signal.signal_type === 'PUT' ? 'text-red-600' : 'text-gray-400'}`}>
                              {signal.signal_type || '-'}
                            </span>
                            <span className="min-w-0 flex-1 text-[11px] leading-tight text-gray-500">
                              E {formatCurrency(signal.entry_price)} · S {formatCurrency(signal.stop_price)} · T {formatCurrency(signal.target_price)}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
              <>
              <p className={`mt-1 text-sm ${autoVerifyDetails.outcome === 'error' ? 'text-red-600' : 'text-gray-600'}`}>
                {autoVerifyDetails.time.toLocaleString()} · {describeAutoVerifyNotification(autoVerifyDetails)}
              </p>
              {autoVerifyDetails.changes?.length > 0 && (
                <ul className="mt-3 max-h-80 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50">
                  {autoVerifyDetails.changes.map(change => (
                    <li key={change.id} className="px-3 py-2 text-sm">
                      <div className="flex items-center gap-3">
                        <span className="w-16 flex-none font-semibold text-gray-800">{change.symbol}</span>
                        <span className={`w-10 flex-none text-xs font-medium ${change.signal_type === 'CALL' ? 'text-green-600' : change.signal_type === 'PUT' ? 'text-red-600' : 'text-gray-400'}`}>
                          {change.signal_type || '-'}
                        </span>
                        <span className="min-w-0 flex-1 text-xs font-semibold text-gray-800">{change.from} → {change.to}</span>
                      </div>
                      <div className="mt-1 pl-[5.25rem] text-xs text-gray-500">
                        Entry {formatCurrency(change.entry_price)} · Stop {formatCurrency(change.stop_price)} · Target {formatCurrency(change.target_price)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              </>
              )}
            </div>
            <div className="flex justify-end rounded-b-xl bg-gray-50 px-5 py-3">
              <button
                type="button"
                autoFocus
                onClick={() => setAutoVerifyDetails(null)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {signalChartScan && (
        <SignalChartModal
          scan={signalChartScan}
          liveQuote={getLiveQuoteForSymbol(livePrices, signalChartScan.symbol)}
          onClose={closeSignalChartModal}
        />
      )}
    </div>
    </div>
  );
};

export default ScanResultsTable;
